import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { API, carsUrl, authFetch } from '../utils/api';
import { getSocket } from '../utils/socket';
// 동시 작업 표시(#343 웹 정합) — carPresence.js 팩토리를 이벤트 접두사만 바꿔 재사용(utils/cancelPresence.js).
import { enter as cancelPresenceEnter, leave as cancelPresenceLeave, createChannel as createCancelPresenceChannel } from '../utils/cancelPresence';
// 표시 헬퍼는 프레즌스 3종 공용 — 정본은 utils/carPresence.js (createPresenceClient 와 같은 자리).
import { mySid as presenceMySid, presenceOthersLabeled } from '../utils/carPresence';
// 모바일(≤768px) 반응형 — 데스크탑 렌더는 그대로 두고 분기만 추가 (2026-08-13)
import { useIsMobile, hScroll } from '../utils/useMobile';
import CustomModal from './CustomModal';
// 동시 작업 표시 점 — 목록 행 공용 (이름 배지 대체, 이름은 title)
import PresenceDot from './PresenceDot';
import PresenceBanner from './PresenceBanner';
// 밑줄 탭 — 말소대행 하위 탭 공용 (정산·세금계산서도 같은 것을 쓴다, Jin 8/26)
import CaHeaderTabs from './CaHeaderTabs';
import CancelAgencyClients from './CancelAgencyClients';
// 작업 유형(단가 마스터) — 거래처 탭의 형제 화면. 정산 수기 입력의 프리셋 원천.
import CancelAgencyWorkTypes from './CancelAgencyWorkTypes';
// 부가서비스 마스터 — 작업 유형 탭 안 형제 섹션(Jin 결정). 신청 폼 3곳의 체크리스트 원천.
import CancelAgencyExtraServices from './CancelAgencyExtraServices';
import ExtraServicePicker from './ExtraServicePicker';
import RequestServicesEditor from './RequestServicesEditor';
// 말소증 보관함(W4) — 폴더 메타포 열람 + 미분류 일괄 귀속 + 말소증 스캔. 네비 '말소증함' 전용 화면.
import CancelAgencyMalsoBox from './CancelAgencyMalsoBox';
// 정산 탭은 장부형 양식(W6)으로 분리 — 프리셋/콤보/VAT 미리보기는 폐차 폼과 공용.
import CancelAgencySettlement, { PresetCombo, EXTRA_SERVICE_PRESETS, calcSettlementPreview } from './CancelAgencySettlement';
// 보강서류 = 대행요청의 하위 작업 — 독립 탭 대신 요청 행에서 여는 시트 (2026-08-26, 앱 정합)
import CancelAgencyDocReqSheet, { docReqSummary } from './CancelAgencyDocReqSheet';
import CancelAgencyDoneList from './CancelAgencyDoneList';
// 대행 요청 상세 패널 — 4세그먼트 + 완료 장부 공용 행 클릭 진입 (2026-09-17)
import CancelRequestDetailPanel from './CancelRequestDetailPanel';
// 운영자 대시보드(작업 큐 + 정산 요약) — 완료 장부와 같은 요약 스트립/카드 문법 (2026-08-26)
import CancelAgencyDashboard from './CancelAgencyDashboard';
// 업무별 기록 (폐차·이전 엑셀 기록, 2026-10-21) — 대표·직원 공용 네비 탭. /work-records* 만 부른다.
import CancelAgencyRecords from './CancelAgencyRecords';
import { PasswordChangeSection, DataManageSection } from './Settings';
// 대량 말소증 = 말소대행 세그먼트의 4번째 화면 (대행업체 갈래만 여기서 마운트, URL 은 /main/bulk-cancel-upload 그대로)
import BulkCancelUploadPage from './BulkCancelUpload';

// 사용 안내는 정적 페이지를 **새 탭**으로 연다. iframe 임베드는 불가 —
// 응답 헤더가 `X-Frame-Options: DENY` + CSP `frame-src` 에 'self' 부재라
// 같은 출처인데도 ERR_BLOCKED_BY_CSP 로 막힌다(실측: 두 가이드 모두 body 접근 불가).
// TaxInvoicePage 의 '사용 방법' iframe 탭도 같은 이유로 동작하지 않는다(별건).
var CA_GUIDE_URL = '/guides/agency-clients-howto.html';

// ── 말소대행 세그먼트 (Jin 8/13) ────────────────────────────────────────────
// 네비 '말소대행' 1개가 3 화면(대행요청·폐차·말소증 업로드)을 묶는다.
//   - 대량말소증은 말소증 업로드로 통합(Jin 8/25) — URL /main/bulk-cancel-upload 만 북마크 호환으로 mount 유지.
//   - 보강서류는 대행요청 행의 배지 → 시트로 흡수(Jin 8/26) — URL /main/ca-docreq 는 ca-requests 로 리다이렉트.
// 전환은 goTab 재사용이라 URL 동기화 그대로. (렌더 없이 계약을 고정하려고 모듈 스코프에 둔다)
export var MALSO_SEGS = [['requests','대행요청'],['scrap','폐차'],['malsoBox','말소증 업로드']];

// 보강서류 요청 체크리스트 (utils/cancelDocRequests.DOC_CHECKLIST 사본)
var DOC_CHECKLIST=['자동차등록증','매매계약서','말소사실증명서','세금계산서','위임장','인감증명서','자동차등록원부','책임보험 해지서류','신분증 사본','기타'];
// 폐차 대행 — 차량 무관 폐차 의뢰. 건별 일회성 정산.
var SCRAP_TYPES=['자진폐차','수출폐차','사고폐차','기타'];
// fee 는 '0' 문자열 default → computeManualItemized 가 itemized 모드(legacy baseFee 회피)
var SCRAP_FORM_DEFAULT={requesterName:'',requesterContact:'',carNo:'',carType:'',vin:'',scrapType:'자진폐차',clientId:'',registrationTax:'0',stampDuty:'0',wonbuFee:'0',serviceFee:'0',reissueFee:'0',otherFee:'0',extraServiceFee:'0',extraServiceNote:'',issueTaxInvoice:true,memo:''};

function CancelAgencyPage({showToast,user,navTab}){
  // 모바일 분기 — 표는 hScroll 래퍼로 가로 스크롤, 다열 grid 는 스택, 버튼은 터치 타깃 40px 확보
  var isMobile = useIsMobile();
  var tapBtn = isMobile?{minHeight:40}:undefined;                       // className="btn" 계열 터치 보정
  var tapRowBtn = isMobile?{minHeight:40,minWidth:40,justifyContent:'center'}:undefined; // 표 안 아이콘 버튼
  // 완료 세그먼트 월 내비 ‹ › 버튼 — CancelAgencySettlement 의 navBtnSt 그대로 (정합, Jin 8/26)
  var reqNavBtnSt = isMobile
    ?{background:'none',border:'1px solid #e2e8f0',borderRadius:8,width:40,height:40,fontSize:15,color:'#475569',cursor:'pointer',padding:0,lineHeight:1,flexShrink:0}
    :{background:'none',border:'1px solid #e2e8f0',borderRadius:6,width:24,height:24,fontSize:13,color:'#475569',cursor:'pointer',padding:0,lineHeight:1,flexShrink:0};
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  // navTab(대행업체 = 상단 네비 통합 모드)이면 그 탭으로 진입. 아니면 cancel_agency 운영자는 대시보드(작업 큐),
  // admin/doc_agency 는 기존 요청 목록. (isCancelAgency 정의 이전이라 인라인 판정)
  var [tab, setTab] = useState(navTab || (user && user.role === 'cancel_agency' ? 'dashboard' : 'requests'));
  var [agencyView, setAgencyView] = useState('info'); // 내 대행사·설정 통합 탭 세그먼트 (Jin 8/12)
  var [requests, setRequests] = useState([]);
  var [agencies, setAgencies] = useState([]);
  // #cap-web: 동시 작업 프레즌스 — {requestId: users[]}. users 는 **접속(소켓) 단위**라 본인 접속(sid)만
  // 빼고, 같은 계정의 다른 접속(PC↔모바일)은 "(PC)"/"(모바일)" 을 붙여 남긴다 (utils/carPresence).
  var [presenceByReq, setPresenceByReq] = useState({});
  // 구독 + 목록 요청을 한 채널로 묶는다 — 스냅샷을 "이 화면이 요청한 id" 로 거르는 주체가 팩토리다.
  var presenceChanRef = useRef(null);
  if (!presenceChanRef.current) presenceChanRef.current = createCancelPresenceChannel();
  var presenceChan = presenceChanRef.current;
  var currentUserId = user && user.id;
  // 내 socket.id 는 렌더당 1회만 읽는다 — 행마다 부르면 500행 목록에서 getSocket() 이 500번
  // (자리만 잡는 빈 점 포함) 불린다. 렌더 중 소켓을 새로 만드는 부수효과도 그만큼 줄어든다.
  var mySid = presenceMySid();
  var othersForReq = function(reqId){
    return presenceOthersLabeled(presenceByReq[reqId]||[], mySid, currentUserId);
  };
  // 서버 미배포/구 서버면 소켓 이벤트가 안 와 presenceByReq 는 항상 빈 채로 유지된다(표시 없음, 에러 없음).
  useEffect(function(){
    var unsub = presenceChan.subscribe(function(evt){
      if(evt.type==='presence'){
        var p=evt.payload||{};
        if(!p.requestId)return;
        setPresenceByReq(function(prev){var n=Object.assign({},prev);n[p.requestId]=p.users||[];return n});
      } else if(evt.type==='snapshot'){
        // 팩토리가 이 채널이 요청한 id 로 걸러 넘긴다 — 담겨 온 것만 반영하고, 값이 비었으면
        // "이제 아무도 없다"로 지운다 (프레즌스 3종 4화면 공통 코드).
        var snap=evt.payload||{};
        setPresenceByReq(function(prev){
          var n=Object.assign({},prev);
          Object.keys(snap).forEach(function(id){if(snap[id]&&snap[id].length)n[id]=snap[id];else delete n[id];});
          return n;
        });
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  // 다른 사용자 작업 중 표시 — 표/카드 공용 (깜빡이는 점, 이름은 title)
  var reqPresenceBadge = function(reqId){
    return <PresenceDot users={othersForReq(reqId)} reserve />;
  };
  // 모달 상단 배너 — 요청 수정/보류/보강서류 시트 공용 (마크업은 components/PresenceBanner.js 한 벌).
  var presenceBanner = function(reqId){
    return <PresenceBanner users={othersForReq(reqId)} target="이 건" />;
  };
  var [actionMenuId, setActionMenuId] = useState(null); // 대행요청 행 '더보기(⋯)' 오버플로우 메뉴 오픈 id
  // 정산 탭 진입 요청 (관리자 대시보드) — 호출마다 새 객체라 같은 대행사 재진입도 반영.
  // {id: 대행사, scope: '' | 'c:<companyId>' | 'w:<clientId>'}
  var [stlFocus, setStlFocus] = useState(null);
  // 폐차 대행 state
  var [scrapList, setScrapList] = useState([]);
  var [showScrapForm, setShowScrapForm] = useState(false);
  var [scrapForm, setScrapForm] = useState(SCRAP_FORM_DEFAULT);
  var [editScrap, setEditScrap] = useState(null);
  var [scrapClients, setScrapClients] = useState([]); // 거래처 드롭다운(walk-in=빈값)
  // 보강서류 요청 state
  var [docReqModal, setDocReqModal] = useState(null); // 발행 대상 {carId, carNo}
  var [docChecks, setDocChecks] = useState([]);
  var [docMemo, setDocMemo] = useState('');
  var [docList, setDocList] = useState([]);
  // 보강서류 시트 열림 대상 = 대행요청 1건 {carId, carNo, cancelType, lienStatus}. 구 'docreq' 탭 대체.
  var [docSheet, setDocSheet] = useState(null);
  // 대행 요청 상세 패널 열림 대상 = 행 데이터(목록 스냅샷) 그대로 — 서버 GET 실패 시 이 값으로 폴백 표시.
  var [detailReq, setDetailReq] = useState(null);
  var [cars, setCars] = useState([]);
  var [stats, setStats] = useState({total:0,pending:0,completed:0,totalFee:0});
  var [seg, setSeg] = useState('new'); // #302 상태 세그먼트 — 앱 cancel_agency_list_screen.dart _inSeg 정합
  var [agencySearch, setAgencySearch] = useState('');
  // 대행요청 탭 기간/업체/정렬 필터 (2026-08-26) — 세그먼트·검색과 AND 결합.
  //   기간 = 완료·보류 공용 월 내비 reqMonth('YYYY-MM') 하나 — 정산 현황의 ‹월› 위젯 정합 (Jin 8/26,
  //   프리셋 칩/직접 범위는 이날 폐지). reqSortKey=null = 기본(트리아지 우선순위). 헤더 클릭으로만 덮어쓴다.
  var [reqMonth, setReqMonth] = useState(function(){var d=new Date();return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')});
  var [showReqMonthInput, setShowReqMonthInput] = useState(false);
  var [reqCompany, setReqCompany] = useState('');
  var [reqSortKey, setReqSortKey] = useState(null);
  var [reqSortDir, setReqSortDir] = useState('asc');
  var [showForm, setShowForm] = useState(false);
  var [showAgencyForm, setShowAgencyForm] = useState(false);
  var [editReq, setEditReq] = useState(null);
  var [editAgency, setEditAgency] = useState(null);
  var [fCarId, setFCarId] = useState('');
  var [fAgencyId, setFAgencyId] = useState('');
  var [fFee, setFFee] = useState('');
  var [fMemo, setFMemo] = useState('');
  var [fDate, setFDate] = useState(new Date().toISOString().slice(0,10));
  var [fServices, setFServices] = useState([]); // 등록 폼 전용 — 편집 중엔 RequestServicesEditor 가 별도 PUT 으로 관리
  var [aName, setAName] = useState('');
  var [aBizNo, setABizNo] = useState('');
  var [aCeo, setACeo] = useState('');
  var [aAddr, setAAddr] = useState('');
  var [aTel, setATel] = useState('');
  var [aEmail, setAEmail] = useState('');
  var [aBankInfo, setABankInfo] = useState('');
  var [aFee, setAFee] = useState('');
  // 정산 폴백 단가 (cancel_agencies.serviceFeeDefault / reissueFeeDefault).
  // 위 aFee(=fee) 와 다른 값 — fee 는 목록 표시용, 이 둘만 utils/cancelSettlement.resolveFees 가 읽는다.
  // '' = 미입력 → PUT body 에서 키를 빼서 서버 기존값을 보존한다(0 은 유효값이라 '' 와 구분 필수).
  var [aServiceFeeDefault, setAServiceFeeDefault] = useState('');
  var [aReissueFeeDefault, setAReissueFeeDefault] = useState('');
  var [aMemo, setAMemo] = useState('');
  var [aOcrLoading, setAOcrLoading] = useState(false);
  var [aOcrPreview, setAOcrPreview] = useState(null);
  var handleAgencyBizLicense = function(file){
    if(!file)return;
    setAOcrLoading(true);
    var reader=new FileReader();reader.onload=function(ev){setAOcrPreview(ev.target.result)};reader.readAsDataURL(file);
    var fd=new FormData();fd.append('image',file);
    authFetch(API+'/admin/companies/ocr-biz-license',{method:'POST',body:fd}).then(function(r){return r.json()}).then(function(data){
      setAOcrLoading(false);
      if(data.error){showToast(data.error,'err');return;}
      if(data.name)setAName(data.name);if(data.ceo)setACeo(data.ceo);if(data.bizNo)setABizNo(data.bizNo);if(data.address)setAAddr(data.address);if(data.tel)setATel(data.tel);
      showToast('사업자등록증 인식 완료');
    }).catch(function(){setAOcrLoading(false);showToast('인식 실패','err')});
  };
  // 업체 계정 관리 상태
  var [users, setUsers] = useState([]);
  var [showUserForm, setShowUserForm] = useState(false);
  var [editUser, setEditUser] = useState(null);
  var [uUserId, setUUserId] = useState('');
  var [uPassword, setUPassword] = useState('');
  var [uName, setUName] = useState('');
  var [uRole, setURole] = useState('업체');
  var [uPhone, setUPhone] = useState('');
  var [uCompanyId, setUCompanyId] = useState('');
  var [selectedIds, setSelectedIds] = useState([]);
  var isCancelAgency = user && user.role === 'cancel_agency';
  var isStaff = isCancelAgency && !!(user && user.agency_staff); // 직원 = 제한 뷰(대행요청+오늘할일+대량말소증)

  // ── 상단 네비 통합 모드 (navTab) ──────────────────────────────────────────
  // 대행업체 계정은 내부 탭바 대신 좌측 네비가 탭이다. 탭 = URL 이라 화면 내부 이동도 URL 을 함께 옮겨
  // 네비 하이라이트(Dashboard 의 pathname→am 동기화)와 브라우저 뒤로가기가 어긋나지 않게 한다.
  // navTab 이 없으면(관리자/doc_agency 의 '대행업체 관리') 기존 내부 탭바 동작 그대로.
  var navigate = useNavigate();
  var NAV_TAB_PATH = {dashboard:'dashboard',requests:'ca-requests',scrap:'ca-scrap',docreq:'ca-docreq',bulk:'bulk-cancel-upload',malsoBox:'ca-malso-box',settlement:'ca-settlement',records:'ca-records',clients:'ca-clients',staff:'ca-staff',agencies:'ca-agency'};
  var goTab = function(t){
    var p = NAV_TAB_PATH[t];
    if(navTab && p && window.location.pathname !== '/main/'+p) navigate('/main/'+p);
    setTab(t);
  };
  useEffect(function(){ if(navTab) setTab(navTab); }, [navTab]);

  // 세그먼트 렌더 — 정산 탭(CancelAgencySettlement 의 viewSeg)과 같은 자리·같은 모양. 목록은 모듈 상단 MALSO_SEGS.
  var isMalsoTab = MALSO_SEGS.some(function(s){return s[0]===tab});
  var [hdrSlotL, setHdrSlotL] = useState(null); // 헤더 좌측 슬롯 (#dash-header-slot-left) — Dashboard 가 그리는 DOM
  // 슬롯 캡처는 반드시 effect 에서. 렌더 본문에서 setState 하면 무한 재렌더(React #301).
  useEffect(function(){ setHdrSlotL(document.getElementById('dash-header-slot-left')||null); },[tab]);
  // 헤더 세그 공용 빌더 — 말소대행/거래처/설정 탭이 같은 문법 (Jin 8/26 통일, 아이콘 없음)
  // 밑줄 탭 마크업은 components/CaHeaderTabs.js 로 승격 (정산·세금계산서와 공용, 2026-08-26).
  var headerSeg = function(items){ return <CaHeaderTabs items={items} isMobile={isMobile}/>; };
  // 말소증 업로드 안내 — 세금계산서 '사용 방법' 과 같은 관례 (정적 페이지 새 탭, 빌드 무관 문서만 갱신).
  // 가이드가 말소증 업로드 화면 전용이라 그 탭에서만 노출 (대행요청/폐차에서 눌러 헷갈리는 것 방지).
  var malsoSeg = headerSeg(MALSO_SEGS.map(function(s){return {label:s[1],on:tab===s[0],onClick:function(){goTab(s[0])}};})
    .concat(tab==='malsoBox' ? [{label:'사용 방법 ↗',href:'/guides/ca-malso-box-howto.html'}] : []));
  var clientsSeg = headerSeg([
    {label:'거래처',on:tab==='clients',onClick:function(){setTab('clients')}},
    {label:'작업 유형',on:tab==='worktypes',onClick:function(){setTab('worktypes')}},
    {label:'사용 방법 ↗',href:CA_GUIDE_URL}
  ]);
  var agencySeg = headerSeg([
    {label:'내 대행사 정보',on:agencyView==='info',onClick:function(){setAgencyView('info')}},
    {label:'계정 설정',on:agencyView==='account',onClick:function(){setAgencyView('account')}}
  ]);

  var toggleSelect = function(id) {
    setSelectedIds(function(prev) {
      return prev.indexOf(id) >= 0 ? prev.filter(function(x){return x!==id}) : prev.concat([id]);
    });
  };
  var toggleSelectAll = function(rows) {
    if (selectedIds.length === rows.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rows.map(function(r){return r.id}));
    }
  };
  // \uC77C\uAD04 \uC791\uC5C5 \uB300\uC0C1 \u2014 \uC120\uD0DD\uB41C \uD589 \uC911 \uC644\uB8CC \uC81C\uC678. (\uC0AD\uC81C\uB294 #302 \uC774\uD6C4 \uADF8\uB8F9 \uD5E4\uB354 \uC804\uC6A9)
  var bulkTargets = function() {
    return filteredReqs.filter(function(r){return selectedIds.indexOf(r.id)>=0 && r.status!=='\uC644\uB8CC'});
  };
  // \uC811\uC218 \uC804\uD658 PUT \u2014 \uD234\uBC14 \uC77C\uAD04 \uC218\uB77D / \uADF8\uB8F9 \uC77C\uAD04 \uC218\uB77D \uACF5\uC6A9(\uD638\uCD9C \uD615\uD0DC \uB4DC\uB9AC\uD504\uD2B8 \uBC29\uC9C0).
  var acceptOne = function(r) {
    var body = {agencyId:r.agencyId, status:'접수', fee:r.fee||0, requestDate:r.requestDate||'', completedDate:r.completedDate||'', memo:r.memo||'', carId:r.carId};
    // #cap-lock 계약: 낙관적 락(version) — 있을 때만 전송(구 서버/undefined 방어). 수락 전이는 '대기'
    //   상태만 대상이라(groupAcceptTargets) 서버 배정 가드(ASSIGNED_TO_OTHER) 대상이 아니다 — force 불필요.
    if(r.version!==undefined&&r.version!==null)body.version=r.version;
    return authFetch(API+'/cancel-requests/'+r.id, {
      method: 'PUT',
      headers: {'Content-Type':'application/json'},
      body: JSON.stringify(body)
    });
  };
  var bulkAccept = function() {
    var targets = bulkTargets();
    if (targets.length === 0) { showToast && showToast('선택된 항목이 없거나 모두 완료상태'); return; }
    if (!window.confirm(targets.length + '건을 일괄 수락하고 접수 상태로 변경할까요?')) return;
    Promise.all(targets.map(acceptOne)).then(function() {
      showToast && showToast('✅ '+targets.length+'건 일괄 수락 완료');
      setSelectedIds([]);
      loadAll();
    }).catch(function() { showToast && showToast('일괄 수락 실패','err'); });
  };
  // #302 (날짜 × 업체) 그룹 일괄 수락 — 그룹 안 '대기' 건만 접수로 전환.
  var groupAccept = function(dKey, comp, rows) {
    var targets = groupAcceptTargets(rows);
    if (targets.length === 0) return;
    setModal({type:'confirm',title:targets.length+'건을 일괄 수락할까요?',message:reqDateLabel(dKey)+' · '+comp+' · 대기 '+targets.length+'건을 접수 상태로 변경합니다.',confirmText:'수락',onConfirm:function(){
      closeModal();
      Promise.all(targets.map(acceptOne)).then(function() {
        showToast && showToast('✅ '+targets.length+'건 일괄 수락 완료');
        loadAll();
      }).catch(function() { showToast && showToast('일괄 수락 실패','err'); });
    }});
  };
  // #302 (\uB0A0\uC9DC \u00D7 \uC5C5\uCCB4) \uADF8\uB8F9 \uC77C\uAD04 \uC0AD\uC81C \u2014 \uADF8\uB8F9\uC5D0 \uBCF4\uC774\uB294 \uD589 \uC804\uBD80(= \uD574\uB2F9 \uB0A0\uC9DC \u00B7 \uC5C5\uCCB4 \u00B7 \uD604\uC7AC \uC138\uADF8\uBA3C\uD2B8).
  //   \uC138\uADF8\uBA3C\uD2B8\uAC00 \uC774\uBBF8 \uC0C1\uD0DC\uB97C \uAC00\uB974\uBBC0\uB85C \uBCC4\uB3C4 \uC0C1\uD0DC \uD544\uD130 \uC5C6\uC74C. \uC644\uB8CC \uC138\uADF8\uBA3C\uD2B8\uB294 \uBC84\uD2BC \uC790\uCCB4\uB97C \uB80C\uB354\uD558\uC9C0 \uC54A\uB294\uB2E4
  //   (DELETE \uB294 \uC644\uB8CC \uC2DC \uC5F0\uACB0\uB41C settlement_item \uC744 \uC815\uB9AC\uD558\uC9C0 \uC54A\uC544 \uC815\uC0B0 \uC7A5\uBD80\uC5D0 \uACE0\uC544 \uD56D\uBAA9\uC774 \uB0A8\uC74C \u2014 routes/cancelAgency.js:1135).
  //   (\uBCD1\uB82C \uC2DC \uAC19\uC740 \uCC28\uB7C9 \uB2E4\uAC74\uC5D0\uC11C \uCC28\uB7C9 \uC0C1\uD0DC \uC6D0\uBCF5 \uCE74\uC6B4\uD2B8 \uACBD\uD569 \u2192 \uC21C\uCC28 \uC720\uC9C0)
  var groupDelete = function(dKey, comp, rows) {
    var targets = rows;
    if (targets.length === 0) return;
    openDelModal(targets.length+'\uAC74\uC744 \uC77C\uAD04 \uC0AD\uC81C\uD560\uAE4C\uC694?',
      reqDateLabel(dKey)+' \u00B7 '+comp+' \u00B7 '+segLabel(seg)+' '+targets.length+'\uAC74 \u2014 \uC0AD\uC81C\uD558\uBA74 \uB418\uB3CC\uB9B4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4. \uD574\uB2F9 \uCC28\uB7C9\uC5D0 \uB0A8\uC740 \uD65C\uC131 \uC694\uCCAD\uC774 \uC5C6\uC73C\uBA74 \uCC28\uB7C9 \uC0C1\uD0DC\uAC00 \'\uB4F1\uB85D\uC644\uB8CC\'\uB85C \uC6D0\uBCF5\uB429\uB2C8\uB2E4.',
      targets.map(function(r){return r.id}));
  };
  // #302 \uC0AD\uC81C \uC0AC\uC720(\uC120\uD0DD) \u2014 \uC0AD\uC81C\uD558\uBA74 \uC5C5\uCCB4\uAC00 \uC62C\uB9B0 \uC694\uCCAD \uC790\uCCB4\uAC00 \uC0AC\uB77C\uC9C0\uBBC0\uB85C \uCDE8\uC18C \uC0AC\uC720\uB97C \uBC1B\uC544
  //   \uC5C5\uCCB4 \uD1B5\uC9C0(\uC11C\uBC84 sendPushToCompany)\uC5D0 \uC2E4\uC5B4 \uBCF4\uB0B8\uB2E4. CustomModal \uC740 \uC785\uB825 \uD544\uB4DC\uB97C \uC9C0\uC6D0\uD558\uC9C0 \uC54A\uC544
  //   \uAC19\uC740 \uD30C\uC77C\uC758 \uBCF4\uB958 \uBAA8\uB2EC(holdModal)\uACFC \uB3D9\uC77C\uD55C \uC778\uB77C\uC778 \uC624\uBC84\uB808\uC774 \uD328\uD134\uC744 \uC4F4\uB2E4.
  var [delModal, setDelModal] = useState(null); // {title, message, ids:[...]}
  var [delReason, setDelReason] = useState('');
  var openDelModal = function(title, message, ids) { setDelReason(''); setDelModal({title:title, message:message, ids:ids}); };
  // \uC21C\uCC28 DELETE \u2014 \uAC1C\uBCC4/\uADF8\uB8F9 \uACF5\uC6A9. (\uBCD1\uB82C \uC2DC \uAC19\uC740 \uCC28\uB7C9 \uB2E4\uAC74\uC5D0\uC11C \uCC28\uB7C9 \uC0C1\uD0DC \uC6D0\uBCF5 \uCE74\uC6B4\uD2B8 \uACBD\uD569 \u2192 \uC21C\uCC28 \uC720\uC9C0)
  //   \uC0AC\uC720\uB294 \uCFFC\uB9AC\uC2A4\uD2B8\uB9C1\uC73C\uB85C \u2014 \uC77C\uBD80 \uD658\uACBD\uC5D0\uC11C DELETE body \uAC00 \uC720\uC2E4\uB41C\uB2E4.
  var confirmDelete = function() {
    var ids = (delModal && delModal.ids) || [];
    var reason = delReason.trim();
    var q = reason ? ('?reason='+encodeURIComponent(reason)) : '';
    setDelModal(null);
    var ok=0, fail=0;
    var step = function(i) {
      if (i >= ids.length) {
        showToast && showToast(fail ? ('\uC0AD\uC81C '+ok+'\uAC74 \uC644\uB8CC / '+fail+'\uAC74 \uC2E4\uD328') : ('\uD83D\uDDD1\uFE0F '+ok+'\uAC74 \uC0AD\uC81C \uC644\uB8CC'), fail?'err':undefined);
        setSelectedIds([]);
        loadAll();
        return;
      }
      authFetch(API+'/cancel-requests/'+ids[i]+q,{method:'DELETE'})
        .then(function(res){ if(res && res.ok===false) fail++; else ok++; })
        .catch(function(){ fail++; })
        .then(function(){ step(i+1); });
    };
    step(0);
  };
  // #302 (날짜 × 업체) 그룹 일괄 상태변경 — 진행중 세그먼트 전용(앱 _statusCompany 정합).
  //   후보는 행 드롭다운과 같은 단계. '보류'는 사유 필수(openHoldModal)라 일괄에서 제외 — 행에서 개별 지정.
  var BULK_STATUS_STEPS = ['대기','접수','수거완료','진행중','완료'];
  var groupStatus = function(dKey, comp, rows, newStatus) {
    var targets = rows;
    if (targets.length === 0 || !newStatus) return;
    setModal({type:'confirm',title:targets.length+'건의 상태를 바꿀까요?',message:reqDateLabel(dKey)+' · '+comp+' · '+targets.length+'건 → \''+statusLabel(newStatus)+'\' 로 변경합니다.',confirmText:'변경',onConfirm:function(){
      closeModal();
      var ok=0, fail=0;
      // 배정 보호(ASSIGNED_TO_OTHER, needsForce) 는 개별 즉시 실패 처리하지 않고 모아서 마지막에 확인 1회 → force 재시도.
      var conflicts=[];
      var finish = function(){
        showToast && showToast(fail ? ('상태변경 '+ok+'건 완료 / '+fail+'건 실패') : (ok+'건 일괄 상태변경 완료'), fail?'err':undefined);
        setSelectedIds([]);
        loadAll();
      };
      var retryForced = function(){
        var i=0;
        var rstep=function(){
          if(i>=conflicts.length){ finish(); return; }
          var c=conflicts[i];
          statusOne(c.row,newStatus,undefined,true).then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
            if(res.status===409){ fail++; } else if(res.data&&res.data.success){ ok++; } else { fail++; }
          }).catch(function(){fail++;}).then(function(){ i++; rstep(); });
        };
        rstep();
      };
      var step = function(i) {
        if (i >= targets.length) {
          if(conflicts.length>0){
            var msg='다음 건은 다른 직원이 담당 중입니다:\n'+conflicts.map(function(c){return '#'+c.row.id+' '+c.staffName;}).join('\n')+'\n\n배정을 해제하고 진행할까요?';
            if(window.confirm(msg)) retryForced();
            else { fail+=conflicts.length; finish(); }
          } else {
            finish();
          }
          return;
        }
        statusOne(targets[i], newStatus).then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
          if(res.status===409 && res.data && res.data.code==='ASSIGNED_TO_OTHER' && res.data.needsForce){
            conflicts.push({row:targets[i], staffName:res.data.staffName||((res.data.conflicts&&res.data.conflicts[0]&&res.data.conflicts[0].staffName)||'')});
          } else if(res.status===409){
            fail++;
          } else if(res.data && res.data.success){
            ok++;
          } else {
            fail++;
          }
        }).catch(function(){ fail++; }).then(function(){ step(i+1); });
      };
      step(0);
    }});
  };

  var loadAll = function(){
    authFetch(API+'/cancel-requests').then(function(r){return r.json()}).then(function(d){var arr=Array.isArray(d)?d:[];setRequests(arr);presenceChan.requestList(arr.map(function(r){return r.id}));}).catch(function(){});
    authFetch(API+'/cancel-agencies').then(function(r){return r.json()}).then(function(d){setAgencies(Array.isArray(d)?d:[])}).catch(function(){});
    authFetch(carsUrl(user)).then(function(r){return r.json()}).then(function(d){setCars(Array.isArray(d)?d:[])}).catch(function(){});
    authFetch(API+'/cancel-requests/stats').then(function(r){return r.json()}).then(function(d){setStats(d&&typeof d==='object'?d:{})}).catch(function(){});
    authFetch(API+'/users').then(function(r){return r.json()}).then(function(d){setUsers(Array.isArray(d)?d:[])}).catch(function(){});
  };
  useEffect(function(){loadAll()},[]);
  // 더보기(⋯) 메뉴 바깥 클릭 시 닫기 — 누수 방지 cleanup 필수
  useEffect(function(){
    var h=function(e){if(!(e.target.closest&&e.target.closest('.cancel-row-actions')))setActionMenuId(null);};
    document.addEventListener('click',h);
    return function(){document.removeEventListener('click',h);};
  },[]);

  // 인증 PDF 열기 — <a href> 네이티브 네비게이션은 JWT(Authorization) 미탑재 → 401. authFetch 로 blob 수신 후 새 탭(팝업 차단 시 다운로드 폴백)
  var openPdf = function(path){
    var w = window.open('', '_blank'); // 클릭 제스처 내 동기 오픈(팝업 차단 회피)
    authFetch(API+path).then(function(r){if(!r.ok)throw new Error('fail');return r.blob()}).then(function(blob){
      var u = URL.createObjectURL(blob);
      if(w){w.location=u;}else{var a=document.createElement('a');a.href=u;a.download='document.pdf';document.body.appendChild(a);a.click();a.remove();}
      setTimeout(function(){URL.revokeObjectURL(u)},60000);
    }).catch(function(){if(w)w.close();showToast('PDF 생성 실패','err')});
  };
  // ── 폐차 대행 ──
  var loadScrap = function(){
    authFetch(API+'/scrap-requests').then(function(r){return r.json()}).then(function(d){setScrapList(Array.isArray(d)?d:[])}).catch(function(){setScrapList([])});
    // 거래처 드롭다운 — 직원(agency_staff)은 /agency-clients 403 이라 생략(walk-in 만)
    if(!isStaff){authFetch(API+'/agency-clients').then(function(r){return r.json()}).then(function(d){setScrapClients(Array.isArray(d)?d:[])}).catch(function(){setScrapClients([])});}
  };
  var clearScrapForm = function(){setShowScrapForm(false);setEditScrap(null);setScrapForm(SCRAP_FORM_DEFAULT);};
  var startEditScrap = function(s){
    setEditScrap(s);
    setScrapForm({requesterName:s.requesterName||'',requesterContact:s.requesterContact||'',carNo:s.carNo||'',carType:s.carType||'',vin:s.vin||'',scrapType:s.scrapType||'자진폐차',clientId:s.clientId?String(s.clientId):'',registrationTax:String(s.registrationTax||0),stampDuty:String(s.stampDuty||0),wonbuFee:String(s.wonbuFee||0),serviceFee:String(s.serviceFee||0),reissueFee:String(s.reissueFee||0),otherFee:String(s.otherFee||0),extraServiceFee:String(s.extraServiceFee||0),extraServiceNote:s.extraServiceNote||'',issueTaxInvoice:s.issueTaxInvoice!==0,memo:s.memo||''});
    setShowScrapForm(true);
  };
  var saveScrap = function(){
    var f=scrapForm;
    var body={requesterName:f.requesterName,requesterContact:f.requesterContact,carNo:f.carNo,carType:f.carType,vin:f.vin,scrapType:f.scrapType,clientId:f.clientId?parseInt(f.clientId):0,registrationTax:parseInt(f.registrationTax)||0,stampDuty:parseInt(f.stampDuty)||0,wonbuFee:parseInt(f.wonbuFee)||0,serviceFee:parseInt(f.serviceFee)||0,reissueFee:parseInt(f.reissueFee)||0,otherFee:parseInt(f.otherFee)||0,extraServiceFee:parseInt(f.extraServiceFee)||0,extraServiceNote:f.extraServiceNote||'',issueTaxInvoice:f.issueTaxInvoice?1:0,memo:f.memo||''};
    var url=editScrap?API+'/scrap-requests/'+editScrap.id:API+'/scrap-requests';
    authFetch(url,{method:editScrap?'PUT':'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json()}).then(function(d){
      if(d.success){showToast(editScrap?'수정 완료':'폐차 접수 완료');clearScrapForm();loadScrap();}else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };
  // 상태 전이(fee 미전송 → 백엔드가 fee 보존). 보류는 사유 모달 경유.
  var updateScrapStatus = function(s, newStatus, holdReason){
    authFetch(API+'/scrap-requests/'+s.id,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({status:newStatus,holdReason:newStatus==='보류'?(holdReason||''):''})}).then(function(r){return r.json()}).then(function(d){
      if(d.success){loadScrap();if(tab==='dashboard')loadDashboard();}else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };
  var deleteScrap = function(id){
    setModal({type:'danger',title:'이 폐차 요청을 삭제할까요?',message:'',confirmText:'삭제',onConfirm:function(){
      closeModal();
      authFetch(API+'/scrap-requests/'+id,{method:'DELETE'}).then(function(r){return r.json()}).then(function(d){if(d.success){showToast('삭제 완료');loadScrap();}else showToast(d.error||'실패','err')}).catch(function(){});
    }});
  };
  // ── 보강서류 요청 (대행사 측) ──
  var loadDocList = function(){
    authFetch(API+'/cancel-doc-requests').then(function(r){return r.json()}).then(function(d){setDocList(Array.isArray(d)?d:[])}).catch(function(){setDocList([])});
  };
  // 차량 케이스별 보강서류 자동추천 (서버 cancelDocRequests.recommendDocs 와 동일 로직 — 사본)
  var recommendDocs = function(cancelType, lienStatus){
    var rec=['자동차등록증','위임장','인감증명서'];
    if(cancelType==='수출말소'){rec.push('매매계약서');rec.push('말소사실증명서');}
    if(lienStatus==='has_lien')rec.push('자동차등록원부');
    var seen={};return rec.filter(function(d){if(seen[d]||DOC_CHECKLIST.indexOf(d)<0)return false;seen[d]=1;return true;});
  };
  var openDocReq = function(carId, carNo, cancelType, lienStatus){ setDocReqModal({carId:carId, carNo:carNo}); setDocChecks(recommendDocs(cancelType, lienStatus)); setDocMemo(''); };
  // 행 배지 → 그 차량의 보강서류 이력 시트. 요청이 0건이어도 열어 신규 발행 동선을 유지한다.
  var openDocSheet = function(r){ setDocSheet({carId:r.carId, carNo:r.carNo, cancelType:r.cancelType, lienStatus:r.lien_status, reqId:r.id}); loadDocList(); };
  // 행 클릭 → 상세 패널. 4세그먼트 표/카드 + 완료 장부(CancelAgencyDoneList) 공용 진입점.
  var openDetail = function(r){ setDetailReq(r); };
  // 보강서류 시트 열림/닫힘에 프레즌스 enter/leave 동기화 (CarList.js 모달 패턴과 동형)
  useEffect(function(){
    if(!docSheet || !docSheet.reqId) return;
    var id = docSheet.reqId;
    cancelPresenceEnter(id);
    return function(){ cancelPresenceLeave(id); };
  },[docSheet?docSheet.reqId:null]);
  var toggleDocCheck = function(label){ setDocChecks(function(prev){ return prev.indexOf(label)>=0 ? prev.filter(function(x){return x!==label}) : prev.concat([label]); }); };
  var submitDocReq = function(){
    if(!docReqModal) return;
    if(docChecks.length===0 && !docMemo.trim()){ showToast('요청 서류 또는 메모를 입력하세요','err'); return; }
    authFetch(API+'/cancel-doc-requests',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({carId:docReqModal.carId,requestedDocs:docChecks,memo:docMemo})}).then(function(r){return r.json()}).then(function(d){
      if(d.success){showToast('보강서류 요청 전송 — 수출업체에 알림');setDocReqModal(null);loadDocList();}
      else showToast(d.error||'요청 실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };
  var completeDocReq = function(id){
    setModal({type:'confirm',title:'보강서류 확인 완료',message:'제출된 서류를 확인하고 완료 처리할까요?',confirmText:'완료',onConfirm:function(){closeModal();
      authFetch(API+'/cancel-doc-requests/'+id+'/complete',{method:'POST'}).then(function(r){return r.json()}).then(function(d){if(d.success){showToast('완료 처리됨');loadDocList();}else showToast(d.error||'실패','err')}).catch(function(){});
    }});
  };
  var cancelDocReq = function(id){
    setModal({type:'confirm',title:'보강서류 요청 취소',message:'이 요청을 취소할까요? 수출업체 인박스에서 사라집니다. (제출 전 요청만 가능)',confirmText:'요청 취소',onConfirm:function(){closeModal();
      authFetch(API+'/cancel-doc-requests/'+id+'/cancel',{method:'POST'}).then(function(r){return r.json()}).then(function(d){if(d.success){showToast('요청 취소됨');loadDocList();}else showToast(d.error||'취소 실패','err')}).catch(function(){});
    }});
  };
  // (메시지 스레드는 시트가 가진다 — CancelAgencyDocReqSheet.js)
  // 운영자 대시보드 — 작업 큐(신규/지연/보류/재교부) + 정산 요약 (단일 집계 API)
  var [dashData, setDashData] = useState(null);
  var loadDashboard = function(){
    authFetch(API+'/cancel-agency/dashboard').then(function(r){return r.json()}).then(function(d){setDashData(d&&typeof d==='object'?d:null)}).catch(function(){setDashData(null)});
  };
  // 관리자(admin/doc_agency) 횡단 대시보드 — 전 대행사 비교
  var [adminDash, setAdminDash] = useState(null);
  var loadAdminDash = function(){
    authFetch(API+'/cancel-agency/admin-dashboard').then(function(r){return r.json()}).then(function(d){setAdminDash(d&&typeof d==='object'?d:null)}).catch(function(){setAdminDash(null)});
  };
  // 직원계정 관리 (cancel_agency) — 같은 대행사 하위 직원 계정
  var [staffList, setStaffList] = useState([]);
  var [showStaffForm, setShowStaffForm] = useState(false);
  var [editStaff, setEditStaff] = useState(null);
  var [sUserId, setSUserId] = useState('');
  var [sPassword, setSPassword] = useState('');
  var [sName, setSName] = useState('');
  var [sPhone, setSPhone] = useState('');
  var loadStaff = function(){
    authFetch(API+'/cancel-agency/staff').then(function(r){return r.json()}).then(function(d){setStaffList(Array.isArray(d)?d:[])}).catch(function(){setStaffList([])});
  };
  var clearStaffForm = function(){setShowStaffForm(false);setEditStaff(null);setSUserId('');setSPassword('');setSName('');setSPhone('');};
  var saveStaff = function(){
    if(!sName){showToast('이름을 입력하세요','err');return;}
    if(editStaff){
      var body={name:sName,phone:sPhone};if(sPassword)body.password=sPassword;
      authFetch(API+'/cancel-agency/staff/'+editStaff.id,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json()}).then(function(d){
        if(d.success){showToast('직원 수정 완료'+(sPassword?' (비밀번호 변경됨)':''));clearStaffForm();loadStaff();}else showToast(d.error||'실패','err');
      }).catch(function(){showToast('서버 오류','err')});
    }else{
      if(!sUserId){showToast('아이디를 입력하세요','err');return;}
      if(!sPassword||sPassword.length<4){showToast('비밀번호는 4자 이상','err');return;}
      authFetch(API+'/cancel-agency/staff',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({userId:sUserId,password:sPassword,name:sName,phone:sPhone})}).then(function(r){return r.json()}).then(function(d){
        if(d.success){showToast('직원 계정 생성 (아이디: '+d.userId+' / 비밀번호: '+sPassword+')');clearStaffForm();loadStaff();}else showToast(d.error||'실패','err');
      }).catch(function(){showToast('서버 오류','err')});
    }
  };
  var startEditStaff = function(u){setEditStaff(u);setSUserId(u.userId);setSPassword('');setSName(u.name);setSPhone(u.phone||'');setShowStaffForm(true);};
  var toggleStaffActive = function(u){
    authFetch(API+'/cancel-agency/staff/'+u.id,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({active:u.active?0:1})}).then(function(r){return r.json()}).then(function(d){
      if(d.success){showToast(u.active?'비활성화':'활성화');loadStaff();}else showToast(d.error||'실패','err');
    }).catch(function(){});
  };
  var toggleStaffRole = function(u){
    var toStaff = !u.agency_staff; // 현재 운영자면 직원으로, 직원이면 운영자로
    setModal({type:'confirm',title:toStaff?'직원 권한으로 변경할까요?':'운영자 권한으로 변경할까요?',message:u.name+' ('+u.userId+')\n'+(toStaff?'경영 정보(정산/거래처/미수)가 숨겨집니다.':'모든 경영 정보를 볼 수 있게 됩니다.')+'\n(해당 사용자는 재로그인 후 적용)',confirmText:'변경',onConfirm:function(){
      closeModal();
      authFetch(API+'/cancel-agency/staff/'+u.id,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({agency_staff:toStaff?1:0})}).then(function(r){return r.json()}).then(function(d){
        if(d.success){showToast('권한 변경 완료 (재로그인 후 적용)');loadStaff();}else showToast(d.error||'실패','err');
      }).catch(function(){showToast('서버 오류','err')});
    }});
  };
  var deleteStaff = function(u){
    setModal({type:'danger',title:'이 직원 계정을 삭제할까요?',message:u.name+' ('+u.userId+')',confirmText:'삭제',onConfirm:function(){
      closeModal();
      authFetch(API+'/cancel-agency/staff/'+u.id,{method:'DELETE'}).then(function(r){return r.json()}).then(function(d){if(d.success){showToast('삭제 완료');loadStaff();}else showToast(d.error||'실패','err')}).catch(function(){});
    }});
  };
  // cancel_agency: 대시보드 탭 진입 시 집계 로드
  useEffect(function(){
    if(tab==='dashboard' && isCancelAgency) loadDashboard();
    if(tab==='admin-dashboard' && !isCancelAgency) loadAdminDash();
    if(tab==='staff' && isCancelAgency) loadStaff();
    if(tab==='scrap') loadScrap();
    // 대행요청 표의 배지 데이터 원천 — 구 'docreq' 탭의 loadDocList 를 여기로 옮겼다.
    if(tab==='requests') loadDocList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab, isCancelAgency]);

  // 팝업 → 수락 완료 시 리스트 자동 갱신 (window custom event)
  useEffect(function() {
    var handler = function() { loadAll(); };
    window.addEventListener('cancel-request-updated', handler);
    return function() { window.removeEventListener('cancel-request-updated', handler); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 소켓 실시간 갱신 — 신규 요청 도착/취소/단계변경/상태변경/배정 시 목록 자동 reload.
  // 기존엔 '수락'(cancel-request-updated)만 reload → 보류·상세보기·신규도착 시 목록이 안 바뀌는 버그.
  // 라이브 팝업과 동일한 소켓을 구독해, 어떤 경로든 DB 최신 상태를 반영.
  // cancel:cancelled(routes/cancelAgency.js DELETE) 와 cancel:request-cancelled(routes/cars-mutate.js
  // 업체측 말소신청 롤백, 별도 payload) 는 서버가 둘 다 emit 하는 서로 다른 이벤트 — 둘 다 구독.
  // 연속 이벤트 폭주(그룹 일괄 처리 등) 대비 300ms debounce + 재연결(connect) 시 1회 즉시 refresh(connect-gap 보정).
  useEffect(function() {
    var sock = getSocket();
    if (!sock) return;
    var t = null;
    var reload = function() { if (t) clearTimeout(t); t = setTimeout(function(){ loadAll(); }, 300); };
    var onConnect = function() { loadAll(); };
    sock.on('cancel:new-request', reload);
    sock.on('cancel:cancelled', reload);
    sock.on('cancel:request-cancelled', reload);
    sock.on('cancel:step', reload);
    sock.on('cancel:updated', reload);
    sock.on('cancel:grabbed', reload);
    sock.on('connect', onConnect);
    return function() {
      if (t) clearTimeout(t);
      try { sock.off('cancel:new-request', reload); } catch(e) {}
      try { sock.off('cancel:cancelled', reload); } catch(e) {}
      try { sock.off('cancel:request-cancelled', reload); } catch(e) {}
      try { sock.off('cancel:step', reload); } catch(e) {}
      try { sock.off('cancel:updated', reload); } catch(e) {}
      try { sock.off('cancel:grabbed', reload); } catch(e) {}
      try { sock.off('connect', onConnect); } catch(e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL ?highlightId=X → 해당 row 스크롤 + 2초 하이라이트
  var location = useLocation();
  var [highlightId, setHighlightId] = useState(null);
  useEffect(function() {
    var qs = new URLSearchParams(location.search);
    var hid = parseInt(qs.get('highlightId'), 10);
    if (hid && requests.length > 0) {
      setHighlightId(hid);
      goTab('requests');
      setTimeout(function() {
        var el = document.getElementById('cancel-req-row-' + hid);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(function() { setHighlightId(null); }, 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, requests.length]);

  // 신청일 기준 경과일 (D+) — 목록 뱃지 + 정렬 키
  var reqAgeDays = function(d){if(!d)return null;var t=new Date(d+'T00:00:00').getTime();if(isNaN(t))return null;return Math.max(0,Math.floor((Date.now()-t)/86400000));};
  // 우선순위 점수 (UX 트리아지 2026-06-21): 완료=최하단 / 활성은 긴급(#323) → 막힘(압류·저당 또는 자격 blocked) → 오래 대기 순
  //   긴급(is_urgent)은 요청자가 직접 체크한 신호라 자동 판정인 막힘보다 큰 가중치. 서버 paged 정렬(routes/cancelAgency.js)과 동일 순서.
  var isUrgentReq = function(r){return !!(r&&(r.is_urgent===1||r.is_urgent==='1'||r.is_urgent===true));};
  var [legendOpen,setLegendOpen]=useState(false); // 표시 안내 시트 (Jin 8/26)
  var reqUrgency = function(r){if(r.status==='완료')return -1;var blocked=r.lien_status==='has_lien'||r.eligibility_status==='blocked';return (isUrgentReq(r)?100000000:0)+(blocked?1000000:0)+(reqAgeDays(r.requestDate)||0);};
  // #302 상태 세그먼트 (앱 cancel_agency_list_screen.dart _inSeg / company_queue.dart:177 정합)
  //   신규='대기' / 진행중=접수·수거완료 / 완료='완료' / 보류='보류'.
  //   '진행중' 은 앱에선 제거된 단계지만 웹 행 드롭다운이 아직 제공 → 어느 탭에도 안 잡혀 사라지는 것 방지용으로 진행중에 포함.
  var QUEUE_ACTIVE_STEPS = ['접수','수거완료'];
  var SEG_ACTIVE_STEPS = QUEUE_ACTIVE_STEPS.concat(['진행중']);
  var SEGMENTS = [{key:'new',label:'신규'},{key:'active',label:'진행중'},{key:'done',label:'완료'},{key:'hold',label:'보류'}];
  var inSeg = function(r, s){
    if(s==='new') return r.status==='대기';
    if(s==='active') return SEG_ACTIVE_STEPS.indexOf(r.status)>=0;
    if(s==='hold') return r.status==='보류';
    return r.status==='완료';
  };
  // 세그먼트 건수 배지 — 검색/필터 적용 전 전체 기준(앱 snap.all 정합)
  var segCounts = {new:0,active:0,done:0,hold:0};
  requests.forEach(function(r){
    if(r.status==='대기') segCounts.new++;
    else if(r.status==='완료') segCounts.done++;
    else if(r.status==='보류') segCounts.hold++;
    else if(SEG_ACTIVE_STEPS.indexOf(r.status)>=0) segCounts.active++;
  });
  var changeSeg = function(k){ setSeg(k); setSelectedIds([]); };
  var segLabel = function(k){ var f=SEGMENTS.filter(function(s){return s.key===k}); return f.length?f[0].label:''; };

  // 표에 보이는 업체 열 = 내 반대편. 대행사 계정은 요청한 수출업체, 수출업체 계정은 대행업체.
  // (기간/업체 필터와 정렬이 이 이름을 쓰므로 filteredReqs 보다 위에 둔다 — var 함수 표현식이라 호이스팅 안 됨)
  var reqCompanyName = function(r){ return (isCancelAgency?r.requesterCompanyName:r.agencyName)||'미지정'; };

  // 기본 정렬 = 트리아지 우선순위(막힌 것·오래된 것 먼저). 헤더 정렬을 고르면 이 자리를 대신한다.
  var reqTriageCmp = function(a,b){
    var ua=reqUrgency(a),ub=reqUrgency(b);
    if(ub!==ua) return ub-ua; // 급한 것 위로(내림차순)
    if(a.status==='완료'&&b.status==='완료') return (b.completedDate||'').localeCompare(a.completedDate||''); // 완료끼리 최신 완료 먼저
    return (b.requestDate||'').localeCompare(a.requestDate||''); // 동점이면 최신 신청 먼저
  };

  // ── 기간/업체 필터 ────────────────────────────────────────────────────────
  // 기준 날짜 열은 세그먼트를 따라간다 — 완료 탭에서 "이번 달"은 이번 달에 **끝낸** 건이 자연스럽다.
  var reqDateField = reqDateFieldFor(seg);
  var reqPeriodUsable = seg==='done' || seg==='hold';   // 신규·진행중은 기간 필터 숨김 (Jin 8/26)
  // 완료·보류 = 월 내비 한 달 고정(정산 정합). 숨은 세그먼트에선 빈 범위 — 몰래 행을 거르지 않게.
  var reqRange = reqPeriodUsable ? reqMonthRange(reqMonth) : {from:'',to:''};
  // 세그먼트까지만 좁힌 모집단 — 업체 드롭다운 건수와 "전체 M건" 의 기준.
  // (세그먼트 배지는 종전대로 필터 무관 전체 기준 = segCounts, 여기서 건드리지 않는다.)
  var segReqs = requests.filter(function(r){ return inSeg(r,seg); });
  var reqCompanyOpts = reqCompanyOptions(segReqs, reqCompanyName);
  // 월 내비는 필터가 아니라 항상 걸린 조회 기준(정산의 월과 같은 축) — 배너/초기화 대상에서 뺀다.
  var reqFilterOn = !!reqCompany || !!agencySearch || !!reqSortKey;
  var resetReqFilters = function(){
    setReqCompany(''); setAgencySearch(''); setReqSortKey(null); setReqSortDir('asc'); setSelectedIds([]);
  };
  var filteredReqs = filterReqRows(segReqs, {
    search: agencySearch, company: reqCompany, nameOf: reqCompanyName,
    dateField: reqDateField, from: reqRange.from, to: reqRange.to
  }).sort(reqSortKey ? cmpReqBy(reqSortKey, reqSortDir, reqCompanyName) : reqTriageCmp);

  // 완료 목록 요약 스트립의 앞머리 문구 — 월 내비가 가리키는 달을 그대로 읽는다 ("이번 달 완료 12건").
  var reqPeriodLabel = (function(){
    var d=new Date(); var cur=d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0');
    if(reqMonth===cur) return '이번 달';
    var p=reqMonth.split('-');
    return (+p[0]===d.getFullYear()?'':p[0]+'년 ')+(+p[1])+'월';
  })();
  // 월 내비 조작 (완료·보류 세그먼트) — 정산 현황의 shiftMonth/monthLabel 과 같은 동작.
  var shiftReqMonth = function(delta){
    var p=reqMonth.split('-'); var dt=new Date(+p[0], +p[1]-1+delta, 1);
    setReqMonth(dt.getFullYear()+'-'+String(dt.getMonth()+1).padStart(2,'0')); setSelectedIds([]);
  };
  var reqMonthTitle = (function(){ var p=reqMonth.split('-'); return p[0]+'년 '+(+p[1])+'월'; })();

  // 헤더 클릭 순환 — 오름차순 → 내림차순 → 기본(트리아지). 거래처 화면(CancelAgencyClients) 관례 그대로.
  var toggleReqSort = function(key){
    if(reqSortKey!==key){ setReqSortKey(key); setReqSortDir('asc'); return; }
    if(reqSortDir==='asc'){ setReqSortDir('desc'); return; }
    setReqSortKey(null); setReqSortDir('asc');
  };
  var reqSortTh = function(label, key, extra){
    var on = reqSortKey===key;
    return (<th key={key} scope="col" style={Object.assign({cursor:'pointer',userSelect:'none'},on?{color:'#1d4ed8'}:null,extra)}
      aria-sort={on?(reqSortDir==='asc'?'ascending':'descending'):'none'}
      title={on&&reqSortDir==='desc'?'클릭하면 기본 정렬로':'클릭해 정렬'}
      onClick={function(){toggleReqSort(key)}}>
      {label}<span style={{marginLeft:4,fontSize:10,color:on?'#1d4ed8':'#cbd5e1'}}>{on?(reqSortDir==='asc'?'▲':'▼'):'↕'}</span>
    </th>);
  };

  // #302 날짜 구분 — 등록시각(createdAt) 우선, 없으면 신청일(requestDate). 정렬은 그대로(트리아지 우선순위) 두고 구분 행만 삽입.
  var reqDateKey = function(r){ return String((r && (r.createdAt || r.requestDate)) || '').slice(0,10); };
  var reqDateLabel = function(k){
    if(!k) return '날짜 미상';
    var d = new Date(k+'T00:00:00');
    if(isNaN(d.getTime())) return k;
    var now = new Date();
    var diff = Math.round((new Date(now.getFullYear(),now.getMonth(),now.getDate()).getTime()-d.getTime())/86400000);
    if(diff===0) return '오늘';
    if(diff===1) return '어제';
    return k.replace(/-/g,'.');
  };
  // #302 (날짜 × 업체) 2단 그룹 — 정렬(트리아지 우선순위)은 그대로 두고 값이 바뀌는 지점에 헤더만 삽입.
  //   그룹 = 헤더 지점부터 날짜·업체가 유지되는 연속 구간. 정렬 때문에 같은 조합이 떨어지면 그룹이 두 번 나온다(허용).
  var reqGroupRows = function(rows, from){
    var d=reqDateKey(rows[from]), c=reqCompanyName(rows[from]), out=[];
    for(var i=from;i<rows.length;i++){
      if(reqDateKey(rows[i])!==d || reqCompanyName(rows[i])!==c) break;
      out.push(rows[i]);
    }
    return out;
  };
  var groupAcceptTargets = function(rows){ return rows.filter(function(r){return r.status==='대기'}); };

  var statusColor = function(s){
    if(s==='완료') return {bg:'#dcfce7',color:'#166534'};
    if(s==='진행중') return {bg:'#fef3c7',color:'#92400e'};
    // 수거완료 — 현장 수거 확보일 뿐 말소완료와 구분. 앱 배지(primaryAccent/tintBlueBg) 정합.
    if(s==='수거완료') return {bg:'#eaf2ff',color:'#1f5fd6'};
    if(s==='접수') return {bg:'#dbeafe',color:'#1e40af'};
    if(s==='보류') return {bg:'#fee2e2',color:'#991b1b'};
    if(s==='취소') return {bg:'#e5e7eb',color:'#6b7280'};
    return {bg:'#f1f5f9',color:'#475569'};
  };

  // 표시 라벨 — DB status 값과 분리 (#251). 수출말소는 번호판·서류만 수거해 가므로 '번호판 수거완료',
  // 폐차말소는 차량 자체를 가져가므로 '차량 수거 완료' (서버 utils/cancelNotify.js 문구와 동일 규칙).
  var statusLabel = function(s, cancelType){
    if(s==='수거완료') return cancelType==='폐차말소'?'차량 수거 완료':'번호판 수거완료';
    return s;
  };

  // 대시보드 작업 큐 행 클릭 → 요청 목록 탭 점프 + 하이라이트 (기존 highlightId 메커니즘 재활용)
  var jumpToRequest = function(reqId){
    goTab('requests');
    setHighlightId(reqId);
    setTimeout(function(){var el=document.getElementById('cancel-req-row-'+reqId);if(el&&el.scrollIntoView)el.scrollIntoView({behavior:'smooth',block:'center'});},120);
    setTimeout(function(){setHighlightId(null);},2600);
  };

  var saveRequest = function(){
    if(!fCarId){showToast('차량을 선택하세요','err');return;}
    if(!editReq && !fAgencyId){showToast('대행업체를 선택하세요','err');return;}
    var body = {carId:parseInt(fCarId), agencyId:fAgencyId?parseInt(fAgencyId):null, fee:parseInt(fFee)||0, requestDate:fDate, memo:fMemo};
    if(!editReq && fServices.length){
      body.services = fServices.map(function(s){ var o={serviceId:s.serviceId}; if(s.memo)o.memo=s.memo; if(s.fee)o.fee=parseInt(s.fee,10)||0; return o; });
    }
    var url = editReq ? API+'/cancel-requests/'+editReq.id : API+'/cancel-requests';
    var method = editReq ? 'PUT' : 'POST';
    if(editReq){
      body.status=editReq.status;body.completedDate=editReq.completedDate||'';body.carId=editReq.carId;
      // #cap-lock 계약: 낙관적 락(version) — 있을 때만 전송(구 서버/undefined 방어).
      if(editReq.version!==undefined&&editReq.version!==null)body.version=editReq.version;
    }
    authFetch(url,{method:method,headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
      var d=res.data||{};
      if(res.status===409&&d.code==='VERSION_CONFLICT'){
        showToast((d.error||'다른 사용자가 이미 수정했습니다')+' — 최신 내용으로 다시 불러왔습니다','err');
        loadAll();
        return;
      }
      if(res.status===409&&d.code==='ASSIGNED_TO_OTHER'){
        if(d.needsForce){
          var list=(d.conflicts&&d.conflicts.length?d.conflicts:[{id:editReq.id,staffName:d.staffName}]);
          if(window.confirm('다음 건은 다른 직원이 담당 중입니다:\n'+list.map(function(c){return '#'+c.id+' '+c.staffName;}).join('\n')+'\n\n배정을 해제하고 진행할까요?')){
            body.force=true;
            authFetch(url,{method:method,headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r2){return r2.json().then(function(d2){return {status:r2.status,data:d2}})}).then(function(res2){
              var d2=res2.data||{};
              if(res2.status===409){
                showToast((d2.error||'다른 사용자가 이미 수정했습니다')+' — 최신 내용으로 다시 불러왔습니다','err');
                loadAll();
                return;
              }
              if(d2.success){showToast('수정 완료');clearReqForm();loadAll();}
              else showToast(d2.error||'실패','err');
            }).catch(function(){showToast('서버 오류','err');});
          }
          return;
        }
        showToast(d.error||'다른 직원이 담당 중입니다','err');
        return;
      }
      if(d.success){showToast(editReq?'수정 완료':'말소 대행 등록 완료');clearReqForm();loadAll();}
      else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err');});
  };

  // 상태 변경 PUT — 행 드롭다운 / 그룹 일괄 상태변경(#302) 공용(페이로드 드리프트 방지).
  //   fee/memo/requestDate/completedDate 는 행 스냅샷을 그대로 재전송한다 — 서버 PUT(routes/cancelAgency.js)
  //   가 미전송 필드를 'b.field || 기본값' 으로 무조건 덮어쓰므로(기존값 보존 안 함) 축소하면 값이 지워진다.
  var statusOne = function(req, newStatus, holdReason, force){
    var body = {agencyId:req.agencyId, status:newStatus, fee:req.fee, requestDate:req.requestDate, completedDate:newStatus==='완료'?new Date().toISOString().slice(0,10):'', memo:req.memo, carId:req.carId, holdReason:newStatus==='보류'?(holdReason||''):''};
    // #cap-lock 계약: 낙관적 락(version) + 배정 보호(force) — 있을 때만 전송(구 서버/undefined 방어).
    if(req.version!==undefined&&req.version!==null)body.version=req.version;
    if(force)body.force=true;
    return authFetch(API+'/cancel-requests/'+req.id,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)});
  };
  var updateStatus = function(req, newStatus, holdReason, force){
    statusOne(req,newStatus,holdReason,force).then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
      var d=res.data||{};
      if(res.status===409&&d.code==='VERSION_CONFLICT'){
        showToast((d.error||'다른 사용자가 이미 수정했습니다')+' — 최신 내용으로 다시 불러왔습니다','err');
        loadAll();
        return;
      }
      if(res.status===409&&d.code==='ASSIGNED_TO_OTHER'){
        if(d.needsForce){
          var list=(d.conflicts&&d.conflicts.length?d.conflicts:[{id:req.id,staffName:d.staffName}]);
          if(window.confirm('다음 건은 다른 직원이 담당 중입니다:\n'+list.map(function(c){return '#'+c.id+' '+c.staffName;}).join('\n')+'\n\n배정을 해제하고 진행할까요?')){
            updateStatus(req,newStatus,holdReason,true);
          }
          return;
        }
        showToast(d.error||'다른 직원이 담당 중입니다','err');
        return;
      }
      if(d.success){loadAll();if(tab==='dashboard')loadDashboard();}
      else showToast(d.error||'상태 변경 실패','err');
    }).catch(function(){showToast('서버 오류','err');});
  };
  // 자격검증(과태료/지방세/의무보험/검사) — 안내용. 불가여도 수거는 진행, 구청 말소접수 전까지 해소하면 됨.
  // 실 API 미연동(위택스/자동차민원포털/보험개발원 키 발급 대기 — LIEN_PRECHECK_GUIDE §6) 상태라
  //    '자격 재조회' 버튼은 숨김 (Jin 2026-08-18: 미구현 기능 노출 금지). 키 발급 + Live 구현 후 true 로.
  var ELIG_RECHECK_ENABLED = false;
  var checkEligibility = function(r){
    authFetch(API+'/cancel-requests/'+r.id+'/eligibility-check',{method:'POST'}).then(function(res){return res.json()}).then(function(d){
      if(!d.success){showToast(d.error||'자격검증 실패','err');return;}
      if(d.status==='ok')showToast((r.carNo||'')+' 말소 가능','');
      else if(d.status==='blocked')showToast((r.carNo||'')+' '+(d.blockers||[]).map(function(b){return b.type}).join(', ')+' — 수거는 진행, 구청 접수 전 해소 필요','');
      else showToast('자격검증 미연동 (API 키 발급 대기)','err');
      loadAll();
    }).catch(function(){showToast('서버 오류','err');});
  };
  // 보류(압류·저당) 사유 선택 모달 상태 — 상태 드롭다운에서 '보류' 선택 시 진입
  var [holdModal, setHoldModal] = useState(null);
  var [holdCat, setHoldCat] = useState('');     // 사유 카테고리(구조화)
  var [holdDetail, setHoldDetail] = useState(''); // 상세 메모(선택)
  var HOLD_REASONS = ['압류','가압류','저당(근저당)','명의·소유권 문제','서류 미비','세금·과태료 미납','지방세체납','차주 연락두절','기타']; // #255 — 지방세체납 별도 사유(자동차세 체납은 말소 접수 차단)
  var openHoldModal = function(r){setHoldModal(r);setHoldCat('');setHoldDetail('');};
  // 폐차 보류 — 동일 모달 재사용(_scrap 플래그로 분기)
  var openScrapHold = function(s){setHoldModal(Object.assign({},s,{_scrap:true}));setHoldCat('');setHoldDetail('');};
  // 보류 모달 열림/닫힘에 프레즌스 enter/leave 동기화. 폐차(_scrap) 는 cancel_requests 프레즌스 대상이 아니다.
  useEffect(function(){
    if(!holdModal || holdModal._scrap) return;
    var id = holdModal.id;
    cancelPresenceEnter(id);
    return function(){ cancelPresenceLeave(id); };
  },[holdModal?(holdModal._scrap?null:holdModal.id):null]);
  var confirmHold = function(){
    if(!holdCat){showToast('사유를 선택하세요','err');return;}
    var reason = holdCat + (holdDetail.trim()?' — '+holdDetail.trim():'');
    if(holdModal._scrap){updateScrapStatus(holdModal,'보류',reason);}else{updateStatus(holdModal,'보류',reason);}
    setHoldModal(null);
  };

  var deleteRequest = function(id){
    openDelModal('이 요청을 삭제할까요?', '삭제하면 되돌릴 수 없습니다. 요청한 업체에 취소 알림이 전달됩니다.', [id]);
  };

  // 완료 되돌리기 — 완료 목록(장부형)엔 상태 드롭다운이 없으니 ⋯ 메뉴가 그 자리를 대신한다.
  // 완료 직전 단계인 '수거완료' 로 돌린다(대기→접수→수거완료→완료). 기존 상태 변경 경로(statusOne)를
  // 그대로 타므로 completedDate 도 함께 비워진다 — 페이로드 드리프트 없음.
  var revertDone = function(r){
    setModal({type:'confirm',title:'완료를 되돌릴까요?',message:(r.carNo||'')+' — \''+statusLabel('수거완료',r.cancelType)+'\' 로 돌아가고 완료일이 지워집니다.',confirmText:'되돌리기',onConfirm:function(){
      closeModal();
      updateStatus(r,'수거완료');
    }});
  };

  var clearReqForm = function(){setShowForm(false);setEditReq(null);setFCarId('');setFAgencyId('');setFFee('');setFMemo('');setFDate(new Date().toISOString().slice(0,10));setFServices([]);};
  var startEditReq = function(r){setEditReq(r);setFCarId(String(r.carId));setFAgencyId(r.agencyId?String(r.agencyId):'');setFFee(String(r.fee||''));setFMemo(r.memo||'');setFDate(r.requestDate||'');setFServices([]);setShowForm(true);};
  // 요청 수정 모달(인라인 폼) 열림/닫힘에 프레즌스 enter/leave 동기화. 신규 등록(editReq 없음)은 대상 없음.
  useEffect(function(){
    if(!showForm || !editReq) return;
    var id = editReq.id;
    cancelPresenceEnter(id);
    return function(){ cancelPresenceLeave(id); };
  },[showForm&&editReq?editReq.id:null]);

  var saveAgency = function(){
    if(!aName){showToast('업체명을 입력하세요','err');return;}
    var body = {name:aName,bizNo:aBizNo,ceo:aCeo,addr:aAddr,tel:aTel,email:aEmail,bankInfo:aBankInfo,fee:parseInt(aFee)||0,memo:aMemo};
    var url = editAgency ? API+'/cancel-agencies/'+editAgency.id : API+'/cancel-agencies';
    var method = editAgency ? 'PUT' : 'POST';
    if(editAgency){
      body.active = editAgency.active;
      // 정산 폴백 단가는 PUT 만 받는다(POST 는 서버가 무시) → 편집 시에만 실어보낸다.
      // 빈칸이면 키 자체를 빼야 서버가 기존값을 보존한다. parseInt('')||0 로 보내면 기존 단가가 0 으로 덮인다.
      if(String(aServiceFeeDefault).trim()!==''){
        var sfd=parseInt(aServiceFeeDefault,10);
        if(!(sfd>=0)){showToast('정산 기본 수수료는 0 이상 정수로 입력하세요','err');return;}
        body.serviceFeeDefault=sfd;
      }
      if(String(aReissueFeeDefault).trim()!==''){
        var rfd=parseInt(aReissueFeeDefault,10);
        if(!(rfd>=0)){showToast('정산 기본 재교부료는 0 이상 정수로 입력하세요','err');return;}
        body.reissueFeeDefault=rfd;
      }
    }
    // authFetch 필수 — 서버가 Bearer 토큰으로 본인 대행사 소유권을 검사한다(무인증 fetch 는 401).
    authFetch(url,{method:method,headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json()}).then(function(d){
      if(d.success){showToast(editAgency?'수정 완료':'업체 등록 완료');clearAgencyForm();loadAll();}
      else showToast(d.error||'실패','err'); // 단가 형식 400 이 조용히 삼켜지지 않도록 (saveUser 와 동일 관례)
    }).catch(function(){showToast('서버 오류','err');});
  };
  var deleteAgency = function(id,name){
    setModal({type:'danger',title:'이 업체를 삭제할까요?',message:name,confirmText:'삭제',onConfirm:function(){
      closeModal();
      authFetch(API+'/cancel-agencies/'+id,{method:'DELETE'}).then(function(){showToast('삭제 완료');loadAll();}).catch(function(){});
    }});
  };
  // 서버 미배포/권한상 컬럼 미포함이면 undefined → '' (폼은 그대로 렌더, 저장 시 키 미전송)
  var feeDefStr = function(v){return (v===null||v===undefined||v==='')?'':String(v);};
  var clearAgencyForm = function(){setShowAgencyForm(false);setEditAgency(null);setAName('');setABizNo('');setACeo('');setAAddr('');setATel('');setAEmail('');setABankInfo('');setAFee('');setAServiceFeeDefault('');setAReissueFeeDefault('');setAMemo('');setAOcrPreview(null);setAOcrLoading(false);};
  // 0 은 유효한 단가라 aFee 의 `String(a.fee||'')` 관례를 쓸 수 없다 (0 → '' 로 뭉개짐) → feeDefStr 로 분리.
  var startEditAgency = function(a){setEditAgency(a);setAName(a.name);setABizNo(a.bizNo);setACeo(a.ceo);setAAddr(a.addr);setATel(a.tel);setAEmail(a.email);setABankInfo(a.bankInfo);setAFee(String(a.fee||''));setAServiceFeeDefault(feeDefStr(a.serviceFeeDefault));setAReissueFeeDefault(feeDefStr(a.reissueFeeDefault));setAMemo(a.memo);setShowAgencyForm(true);};
  // 대행사 계정은 '내 대행사 정보' — 목록/신규 등록 없이 본인 1건만 폼으로. (서버도 본인 행만 전체 컬럼 반환)
  var myAgency = isCancelAgency ? agencies.find(function(a){return a.id===(user&&user.agencyId)}) : null;
  useEffect(function(){
    if(tab==='agencies'&&myAgency&&(!editAgency||editAgency.id!==myAgency.id))startEditAgency(myAgency);
  },[tab,myAgency]); // eslint-disable-line react-hooks/exhaustive-deps

  // 업체 계정 관리 함수
  var companyUsers = users.filter(function(u){return u.role==='업체'||u.role==='대행업체'});
  var saveUser = function(){
    if(!uUserId){showToast('아이디를 입력하세요','err');return;}
    if(!uName){showToast('이름을 입력하세요','err');return;}
    var body = {userId:uUserId,name:uName,role:uRole,phone:uPhone,companyId:parseInt(uCompanyId)||0};
    if(editUser){
      body.active = editUser.active;
      authFetch(API+'/users/'+editUser.id,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json()}).then(function(d){
        if(d.success){showToast('계정 수정 완료');clearUserForm();loadAll();}
        else showToast(d.error||'실패','err');
      }).catch(function(){showToast('서버 오류','err');});
    } else {
      body.password = uPassword || '1234';
      authFetch(API+'/users',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json()}).then(function(d){
        if(d.success){showToast('계정 생성 완료 (비밀번호: '+(uPassword||'1234')+')');clearUserForm();loadAll();}
        else showToast(d.error||'실패','err');
      }).catch(function(){showToast('서버 오류','err');});
    }
  };
  var deleteUser = function(id,name){
    setModal({type:'danger',title:'이 계정을 삭제할까요?',message:name,confirmText:'삭제',onConfirm:function(){
      closeModal();
      authFetch(API+'/users/'+id,{method:'DELETE'}).then(function(){showToast('삭제 완료');loadAll();}).catch(function(){});
    }});
  };
  var toggleUserActive = function(u){
    var body = {name:u.name,role:u.role,phone:u.phone,companyId:u.companyId,active:u.active?0:1};
    authFetch(API+'/users/'+u.id,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json()}).then(function(d){
      if(d.success){showToast(body.active?'활성화':'비활성화');loadAll();}
    }).catch(function(){});
  };
  var clearUserForm = function(){setShowUserForm(false);setEditUser(null);setUUserId('');setUPassword('');setUName('');setURole('업체');setUPhone('');setUCompanyId('');};
  var startEditUser = function(u){setEditUser(u);setUUserId(u.userId);setUName(u.name);setURole(u.role);setUPhone(u.phone||'');setUCompanyId(String(u.companyId||''));setShowUserForm(true);};


  // ── 대행요청 행 조각 — 표(데스크탑)와 카드(모바일)가 공유한다 ─────────────────
  // 390px 실측: 11열 표는 가로 스크롤 폭 940 / 보이는 폭 316 → 차량·차대번호·소유자 3열만 보인다.
  // 모바일만 카드 목록으로 바꾸되, 데이터·핸들러는 아래 조각을 함께 써서 드리프트를 막는다.
  // (데스크탑 렌더는 여기로 옮기기 전과 완전히 동일 — extra 는 모바일에서만 넘긴다)
  var reqBadges = function(r){ return (<React.Fragment>{isUrgentReq(r)&&<span style={{marginLeft:6,padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:'#fee2e2',color:'#991b1b'}} title="요청자가 긴급으로 신청한 건">긴급</span>}{r.cancelType==='폐차말소'&&<span style={{marginLeft:6,padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:'#fef2f2',color:'#b91c1c'}}>폐차</span>}{r.lien_status==='has_lien'&&<span style={{marginLeft:4,padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:'#fee2e2',color:'#b91c1c'}} title={r.lien_detail||'압류/저당'}>압류/저당</span>}{r.eligibility_status==='blocked'&&<span style={{marginLeft:4,padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:'#fffbeb',color:'#b45309',border:'1px solid #fde68a'}} title={(r.eligibility_detail||'')+' (수거 진행, 접수 전 해소)'}>해소 필요</span>}{r.eligibility_status==='ok'&&<span style={{marginLeft:4,padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:'#dcfce7',color:'#15803d'}} title="자격 확인 — 말소 가능">가능</span>}{r.serviceCount>0&&<span style={{marginLeft:4,padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:'#eff6ff',color:'#1d4ed8'}}>부가 {r.serviceCount}</span>}</React.Fragment>); };
  var reqStatusSelect = function(r, extra){ return (
    <select value={r.status} disabled={r.status==='취소'} title={r.status==='취소'?'취소된 요청입니다. 되살릴 수 없으니 다시 신청해 주세요':undefined} onChange={function(e){if(e.target.value==='보류'){openHoldModal(r);}else{updateStatus(r,e.target.value);}}} style={Object.assign({padding:isMobile?'8px 6px':'3px 6px',minHeight:isMobile?40:undefined,border:'1px solid #d0d5dd',borderRadius:4,fontSize:isMobile?12:11,marginRight:4,background:r.status==='취소'?'#f1f5f9':undefined,color:r.status==='취소'?'#94a3b8':undefined},extra)}>
      <option value="대기">대기</option><option value="접수">접수</option><option value="수거완료">{statusLabel('수거완료',r.cancelType)}</option><option value="진행중">진행중</option><option value="보류">보류</option><option value="완료">완료</option>{r.status==='취소'&&<option value="취소">취소(업체요구)</option>}
    </select>); };
  // 보강서류 배지 — 그 요청의 이력 시트를 연다(구 '보강서류' 탭 흡수). 0건이면 '서류 +' = 신규 발행 진입
  var reqDocBtn = function(r, extra){ var db=docReqSummary(docList,r.carId); return (
    <button className="btn btn-outline btn-sm" onClick={function(){openDocSheet(r)}} title={db.title}
      style={Object.assign({marginRight:4},tapRowBtn,db.tone==='blue'?{background:'#eff6ff',borderColor:'#93c5fd',color:'#1d4ed8',fontWeight:700}:null,extra)}>{db.label}</button>); };
  var reqMoreMenu = function(r, extra){ return (
    <span className="cancel-row-actions" style={Object.assign({position:'relative',display:'inline-block'},extra)}>
      <button className="btn btn-outline btn-sm" title="더보기" style={tapRowBtn} onClick={function(){setActionMenuId(actionMenuId===r.id?null:r.id)}}>⋯</button>
      {actionMenuId===r.id&&(<div style={{position:'absolute',right:0,top:'100%',marginTop:4,background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.12)',zIndex:50,minWidth:130,padding:4,textAlign:'left'}}>
        {ELIG_RECHECK_ENABLED&&<button className="btn btn-sm" style={Object.assign({display:'block',width:'100%',marginBottom:4,textAlign:'left',border:'none',background:'transparent'},tapBtn)} onClick={function(){setActionMenuId(null);checkEligibility(r);}}>자격 재조회</button>}
        {!isStaff&&<button className="btn btn-sm" style={Object.assign({display:'block',width:'100%',marginBottom:4,textAlign:'left',border:'none',background:'transparent'},tapBtn)} onClick={function(){setActionMenuId(null);startEditReq(r);}}>수정</button>}
        <button className="btn btn-sm" style={Object.assign({display:'block',width:'100%',textAlign:'left',border:'none',background:'transparent',color:'#b91c1c'},tapBtn)} onClick={function(){setActionMenuId(null);deleteRequest(r.id);}}>삭제</button>
      </div>)}
    </span>); };
  // (날짜 × 업체) 그룹 바 — 표의 구분 행 안, 모바일에선 카드 위 밴드. 일괄 수락/상태변경/삭제가 여기 붙는다.
  var reqGroupBar = function(dKey, comp, gRows){ return (
    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:isMobile?'wrap':undefined}}>
      <span style={{fontSize:12,fontWeight:700,color:'#334155'}}>{comp}</span>
      <span style={{fontSize:11,color:'#64748b'}}>{gRows.length}건</span>
      {seg==='new'&&groupAcceptTargets(gRows).length>0&&<button className="btn btn-sm" style={Object.assign({marginLeft:4,background:'#16a34a',color:'#fff',border:'none'},tapBtn)} onClick={function(){groupAccept(dKey,comp,gRows)}}>일괄 수락</button>}
      {seg==='active'&&gRows.length>0&&(<select value="" onChange={function(e){var v=e.target.value;e.target.value='';groupStatus(dKey,comp,gRows,v);}} style={{marginLeft:4,padding:isMobile?'8px 6px':'3px 6px',minHeight:isMobile?40:undefined,border:'1px solid #d0d5dd',borderRadius:4,fontSize:12,fontWeight:700,color:'#1e40af'}}>
        <option value="">일괄 상태변경</option>
        {BULK_STATUS_STEPS.map(function(s){return(<option key={s} value={s}>{statusLabel(s)}</option>)})}
      </select>)}
      {/* 삭제는 우측 끝 분리 배치 — 주 액션 오클릭 방지. 완료 세그먼트는 정산 항목 고아화 위험으로 제외 */}
      {seg!=='done'&&gRows.length>0&&<button className="btn btn-sm" title={reqDateLabel(dKey)+' · '+comp+' · '+segLabel(seg)+' '+gRows.length+'건 일괄 삭제'} style={Object.assign({marginLeft:'auto',background:'#e24b4a',color:'#fff',border:'none'},tapBtn)} onClick={function(){groupDelete(dKey,comp,gRows)}}>일괄 삭제</button>}
    </div>); };
  // walk-in 폐차 상태 드롭다운 — 표/카드 공용 (전이 4단계는 대행요청과 다르다)
  var scrapStatusSelect = function(s, extra){ return (
    <select value={s.status} onChange={function(e){if(e.target.value==='보류'){openScrapHold(s);}else{updateScrapStatus(s,e.target.value);}}} style={Object.assign({padding:isMobile?'8px 6px':'3px 6px',minHeight:isMobile?40:undefined,border:'1px solid #d0d5dd',borderRadius:4,fontSize:isMobile?12:11,marginRight:4},extra)}>
      <option value="접수">접수</option><option value="진행중">진행중</option><option value="보류">보류</option><option value="완료">완료</option>
    </select>); };
  // 모바일 카드 1장 (대행요청·폐차) — 표의 한 행과 같은 데이터
  var mReqCard={background:'#fff',border:'1px solid #e5e7eb',borderRadius:10,padding:'10px 12px',marginBottom:8};
  var mEllip={whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'};
  // 모바일 카드 한 줄 = [좌: 정보(flex, 말줄임)] … [우: 상태/금액/날짜(nowrap)].
  // 세로 나열만 하면 390 에서 우측 절반이 그대로 빈다(Jin 실기) → 완료 장부(DoneList) 카드와 같은 문법.
  var mCardRow={display:'flex',alignItems:'center',gap:8};
  var mCardL=Object.assign({flex:1,minWidth:0},mEllip);
  var mCardR={flexShrink:0,whiteSpace:'nowrap',textAlign:'right'};

  var ls={display:'block',fontSize:11,fontWeight:600,color:'#64748b',marginBottom:4,marginTop:10,fontFamily:'Pretendard,sans-serif'};
  var is={width:'100%',padding:'8px 10px',border:'1px solid #d0d5dd',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'Pretendard,sans-serif'};
  var cs={display:'block',marginTop:4,fontSize:isMobile?12:10.5,lineHeight:1.45,color:'#94a3b8',fontFamily:'Pretendard,sans-serif'}; // 입력칸 힌트 (CancelAgencyClients capSt 관례)
  // 대행사 정보 폼 섹션 구분 — 필드 11개가 한 그리드에 평평하게 깔려 금액 3종(표시용/정산 폴백 2)이
  // 뒤섞여 보이던 것을 그룹으로 나눈다. 그리드는 하나로 유지하고(열 정렬 보존) 헤더만 full-width 로 끼운다.
  var secSt={gridColumn:'1 / -1',display:'flex',alignItems:'center',gap:8,marginTop:isMobile?14:20,marginBottom:2,fontFamily:'Pretendard,sans-serif'};
  var secTx={fontSize:12.5,fontWeight:800,color:'#1e3a5f',whiteSpace:'nowrap'};
  var secLn={flex:1,height:1,background:'#e5e7eb'};
  // 폴백 안내는 **full-width 유지 필수** — span 2 로 줄이면 뒤따르는 단가 칸이 같은 행 3열로 흘러들어
  // 재교부료가 다음 줄로 밀린다(브라우저 확인). 이 노트가 행 구분자 역할을 겸한다.
  var noteSt={gridColumn:'1 / -1',background:'#f8fafc',border:'1px solid #e5e7eb',borderRadius:8,padding:'8px 10px',fontSize:isMobile?12:11,lineHeight:1.5,color:'#64748b',fontFamily:'Pretendard,sans-serif'};
  var sec=function(title){return(<div style={secSt}><span style={secTx}>{title}</span><div style={secLn}/></div>)};
  var FORM_MAX_W=1200; // 대행사 정보 폼 최대폭 (세그먼트 버튼 줄·로딩 카드와 공유 — 왼쪽 끝 정렬 유지)
  var wide={gridColumn:isMobile?undefined:'span 2'}; // 3열 중 2열 — 계좌/메모처럼 긴 텍스트용(전폭은 과하게 늘어남)
  var fmt=function(n){return(parseInt(n)||0).toLocaleString()};


  return (
    <div style={{fontFamily:'Pretendard,-apple-system,BlinkMacSystemFont,sans-serif'}}>
      {/* 타이틀 배너 좌측 — [대행요청|폐차|말소증 업로드]. 네비 통합 모드(navTab) 의 말소대행 3 화면에서만. */}
      {!!navTab&&isMalsoTab&&!!hdrSlotL&&ReactDOM.createPortal(
        <React.Fragment key="ca-header-left">{malsoSeg}</React.Fragment>,
        hdrSlotL)}
      {!!navTab&&(tab==='clients'||tab==='worktypes')&&!!hdrSlotL&&ReactDOM.createPortal(clientsSeg, hdrSlotL)}
      {!!navTab&&tab==='agencies'&&isCancelAgency&&!!hdrSlotL&&ReactDOM.createPortal(agencySeg, hdrSlotL)}
      {/* 통계 카드 — cancel_agency 운영자는 대시보드 KPI 가 대체하므로 숨김 (중복 방지) */}
      {!isCancelAgency && (<div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(4,1fr)',gap:isMobile?10:16,marginBottom:20}}>
        <div className="stat-card"><div className="stat-info"><div className="stat-number">{stats.total}</div><div className="stat-label">전체 요청</div></div></div>
        <div className="stat-card"><div className="stat-info"><div className="stat-number">{stats.pending}</div><div className="stat-label">진행중</div></div></div>
        <div className="stat-card"><div className="stat-info"><div className="stat-number">{stats.completed}</div><div className="stat-label">완료</div></div></div>
        <div className="stat-card"><div className="stat-info"><div className="stat-number">{fmt(stats.totalFee)}</div><div className="stat-label">총 수수료</div></div></div>
      </div>)}

      {/* 탭 — 상단 네비 통합 모드(navTab)에선 네비가 탭을 대신하므로 렌더하지 않는다 */}
      {!navTab && (<div style={{display:'flex',gap:4,marginBottom:16,justifyContent:'center',flexWrap:'wrap'}}>
        {isCancelAgency && <button className={'btn '+(tab==='dashboard'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('dashboard')}}>대시보드</button>}
        {!isCancelAgency && <button className={'btn '+(tab==='admin-dashboard'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('admin-dashboard')}}>대시보드</button>}
        <button className={'btn '+(tab==='requests'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('requests')}}>대행 요청</button>
        <button className={'btn '+(tab==='scrap'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('scrap')}}>폐차</button>
        {/* 정산 = 미수 보드(전체) + 거래처 장부 통합 워크스페이스 — 구 '미수' 탭은 여기로 흡수됐다 */}
        {!isStaff && <button className={'btn '+(tab==='settlement'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('settlement')}}>정산</button>}
        {!isStaff && <button className={'btn '+(tab==='agencies'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('agencies')}}>{isCancelAgency?'내 대행사 정보':'업체 관리'}</button>}
        {!isStaff && <button className={'btn '+(tab==='clients'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('clients')}}>거래처</button>}
        {!isStaff && <button className={'btn '+(tab==='worktypes'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('worktypes')}}>작업 유형</button>}
        {!isStaff && <a className="btn btn-outline" style={Object.assign({textDecoration:'none'},tapBtn)} href={CA_GUIDE_URL} target="_blank" rel="noopener noreferrer">사용 방법 ↗</a>}
        {isCancelAgency && !isStaff && <button className={'btn '+(tab==='staff'?'btn-blue':'btn-outline')} style={tapBtn} onClick={function(){setTab('staff')}}>직원</button>}
      </div>)}

      {/* ── 운영자 대시보드 탭 (작업 큐 우선) — 화면은 CancelAgencyDashboard.js (데이터/동선 동일, 표현만 장부 톤) ── */}
      {tab==='dashboard' && isCancelAgency && (
        <CancelAgencyDashboard
          data={dashData}
          isMobile={isMobile}
          isStaff={isStaff}
          presenceBadge={reqPresenceBadge}
          onOpenRequest={jumpToRequest}
          onGoScrap={function(){goTab('scrap')}}
          onGoSettlement={function(scope){setStlFocus({id:(user&&user.agencyId)||0,scope:scope||''});goTab('settlement');}}
        />)}

      {/* ── 관리자 횡단 대시보드 탭 (admin/doc_agency) ── */}
      {tab==='admin-dashboard' && !isCancelAgency && (function(){
        var ad = adminDash;
        if(!ad){return(<div className="card" style={{padding:40,textAlign:'center',color:'#94a3b8'}}>불러오는 중…</div>);}
        var t = ad.totals||{};
        var agencies2 = ad.agencies||[];
        var kpiCard2=function(num,label,danger){return(<div className="stat-card" key={label}><div className="stat-info"><div className="stat-number" style={danger&&num>0?{color:'#dc2626'}:undefined}>{num}</div><div className="stat-label">{label}</div></div></div>);};
        var goAgency=function(aid){setStlFocus({id:aid});setTab('settlement');};
        return(<div>
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(5,1fr)',gap:isMobile?10:12,marginBottom:18}}>
            {kpiCard2(t.agencyCount||0,'대행사')}
            {kpiCard2(t.overdue||0,'지연 (접수 D+1+)',true)}
            {kpiCard2(t.hold||0,'보류')}
            {kpiCard2(t.monthCompleted||0,'이번달 완료')}
            {kpiCard2(fmt(t.receivable),'미수 합계')}
          </div>
          <div className="card" style={isMobile?{padding:12}:undefined}>
            <div style={isMobile?hScroll:undefined}>
            <table className="data-table sticky-head" style={isMobile?{minWidth:700}:undefined}>
              <thead><tr>
                <th>대행사</th><th style={{textAlign:'right'}}>진행중</th><th style={{textAlign:'right'}}>지연</th><th style={{textAlign:'right'}}>보류</th>
                <th style={{textAlign:'right'}}>이번달 완료</th><th style={{textAlign:'right'}}>이번달 청구</th><th style={{textAlign:'right'}}>미수</th>
              </tr></thead>
              <tbody>
                {agencies2.length>0?agencies2.map(function(a){return(
                  <tr key={a.agencyId} onClick={function(){goAgency(a.agencyId);}} style={{cursor:'pointer'}}>
                    <td style={{fontWeight:700}}>{a.name}{a.active?'':<span style={{color:'#94a3b8',fontSize:11}}> (비활성)</span>}</td>
                    <td style={{textAlign:'right'}}>{a.inProgress}</td>
                    <td style={{textAlign:'right',color:a.overdue>0?'#dc2626':'#475569',fontWeight:a.overdue>0?700:400}}>{a.overdue}</td>
                    <td style={{textAlign:'right',color:a.hold>0?'#991b1b':'#475569'}}>{a.hold}</td>
                    <td style={{textAlign:'right'}}>{a.monthCompleted}</td>
                    <td style={{textAlign:'right'}}>{fmt(a.monthBilled)}</td>
                    <td style={{textAlign:'right',fontWeight:700,color:a.receivable>0?'#dc2626':'#16a34a'}}>{fmt(a.receivable)}</td>
                  </tr>);}):<tr><td colSpan={7} style={{textAlign:'center',color:'#94a3b8',padding:20}}>등록된 대행사 없음</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
          <p style={{fontSize:12,color:'#94a3b8',marginTop:8}}>행 클릭 → 해당 대행사 정산 상세</p>
        </div>);
      })()}

      {/* ── 직원계정 관리 탭 (cancel_agency) ── */}
      {tab==='staff' && isCancelAgency && (<div>
        <div style={{display:'flex',flexWrap:isMobile?'wrap':undefined,gap:isMobile?10:undefined,justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <span style={{fontSize:13,color:'#64748b'}}>대행사 직원 로그인 계정을 관리합니다. 직원은 같은 대행사 업무를 함께 처리합니다.</span>
          <button className="btn btn-green" style={tapBtn} onClick={function(){clearStaffForm();setShowStaffForm(true)}}>직원 추가</button>
        </div>
        {showStaffForm && (<div className="card" style={{padding:isMobile?16:20,marginBottom:16,border:'2px solid #3b82f6'}}>
          <h3 style={{margin:'0 0 12px'}}>{editStaff?'직원 수정':'직원 추가'}</h3>
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:12}}>
            <div><label style={ls}>아이디*</label><input style={is} value={sUserId} onChange={function(e){setSUserId(e.target.value)}} disabled={!!editStaff} placeholder="로그인 아이디"/></div>
            <div><label style={ls}>{editStaff?'비밀번호 변경 (선택)':'비밀번호*'}</label><input style={is} type="text" value={sPassword} onChange={function(e){setSPassword(e.target.value)}} placeholder={editStaff?'변경 시에만 입력':'4자 이상'}/></div>
            <div><label style={ls}>이름*</label><input style={is} value={sName} onChange={function(e){setSName(e.target.value)}}/></div>
            <div><label style={ls}>전화</label><input style={is} value={sPhone} onChange={function(e){setSPhone(e.target.value)}}/></div>
          </div>
          <div style={{display:'flex',gap:8,marginTop:16}}>
            <button className="btn btn-blue" style={tapBtn} onClick={saveStaff}>{editStaff?'수정':'생성'}</button>
            <button className="btn btn-outline" style={tapBtn} onClick={clearStaffForm}>취소</button>
          </div>
        </div>)}
        <div className="card" style={isMobile?{padding:12}:undefined}>
          <div style={isMobile?hScroll:undefined}>
          <table className="data-table sticky-head" style={isMobile?{minWidth:640}:undefined}>
            <thead><tr><th>아이디</th><th>이름</th><th>전화</th><th>권한</th><th>상태</th><th>관리</th></tr></thead>
            <tbody>
              {staffList.map(function(u){var isStaffRow=!!u.agency_staff;return(
                <tr key={u.id} style={{opacity:u.active?1:0.5}}>
                  <td style={{fontWeight:700}}>{u.userId}{u.id===user.id&&<span style={{color:'#3b82f6',fontSize:11}}> (나)</span>}</td>
                  <td>{u.name}</td>
                  <td>{u.phone||'-'}</td>
                  <td><span style={{padding:'3px 8px',borderRadius:10,fontSize:11,fontWeight:700,background:isStaffRow?'#fef9c3':'#dbeafe',color:isStaffRow?'#854d0e':'#1e40af'}}>{isStaffRow?'직원':'운영자'}</span></td>
                  <td><span style={{padding:'3px 8px',borderRadius:10,fontSize:11,fontWeight:700,background:u.active?'#dcfce7':'#f1f5f9',color:u.active?'#166534':'#94a3b8'}}>{u.active?'활성':'비활성'}</span></td>
                  <td style={isMobile?{whiteSpace:'nowrap'}:undefined}>
                    <button className="btn btn-outline btn-sm" onClick={function(){startEditStaff(u)}} style={Object.assign({marginRight:4},tapRowBtn)}>수정</button>
                    {u.id!==user.id&&<button className="btn btn-outline btn-sm" onClick={function(){toggleStaffRole(u)}} style={Object.assign({marginRight:4},tapBtn)} title="권한 전환">{isStaffRow?'운영자':'직원'}</button>}
                    {u.id!==user.id&&<button className="btn btn-outline btn-sm" onClick={function(){toggleStaffActive(u)}} style={Object.assign({marginRight:4},tapRowBtn)}>{u.active?'중지':'활성'}</button>}
                    {u.id!==user.id&&<button className="btn btn-outline btn-sm btn-danger" onClick={function(){deleteStaff(u)}} style={tapRowBtn}>삭제</button>}
                  </td>
                </tr>)})}
              {staffList.length===0&&(<tr><td colSpan="6" style={{padding:40,textAlign:'center',color:'#94a3b8'}}>등록된 직원 계정이 없습니다.</td></tr>)}
            </tbody>
          </table>
          </div>
        </div>
      </div>)}

      {/* ── 거래처 관리 / 작업 유형 탭 ──
          네비 통합 모드(navTab)에선 내부 탭바가 없다 → 두 화면을 잇는 세그먼트를 여기서 렌더.
          '작업 유형' 은 네비 항목이 없어 URL 은 /main/ca-clients 그대로 둔다 (setTab 만). */}
      {/* 거래처/작업 유형/사용 방법 세그는 헤더 슬롯 포털로 이동 (Jin 8/26 통일) */}
      {tab==='clients' && (
        <CancelAgencyClients showToast={showToast} user={user} agencies={agencies} isCancelAgency={isCancelAgency} />
      )}
      {tab==='worktypes' && (
        <div>
          <CancelAgencyWorkTypes showToast={showToast} agencies={agencies} isCancelAgency={isCancelAgency} />
          <CancelAgencyExtraServices showToast={showToast} isCancelAgency={isCancelAgency} isStaff={isStaff} />
        </div>
      )}
      {/* ── 말소증 보관함 탭 (W4) — 폴더 목록 / 폴더 상세 / 미분류 작업 큐 + 스캔 ── */}
      {tab==='malsoBox' && (
        <CancelAgencyMalsoBox showToast={showToast} user={user} agencies={agencies} isCancelAgency={isCancelAgency} />
      )}
      {/* ── 업무별 기록 탭 — 대표·직원 공용 (Jin 10/21 결정 5) ── */}
      {tab==='records' && <CancelAgencyRecords showToast={showToast} user={user} />}
      {/* 사용 방법 — public/guides/agency-clients-howto.html 정적 페이지를 iframe 으로.
          TaxInvoicePage 의 '사용 방법' 탭과 동일 관례(빌드 무관, 문서만 갱신하면 반영). */}

      {/* ── 대행 요청 탭 ── */}
      {legendOpen && (
        <div role="dialog" aria-label="표시 안내" onClick={function(){setLegendOpen(false)}}
          style={{position:'fixed',inset:0,zIndex:1300,background:'rgba(15,23,42,.55)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div onClick={function(e){e.stopPropagation()}}
            style={{background:'#fff',borderRadius:12,width:'min(560px,94vw)',maxHeight:'86vh',overflowY:'auto',padding:'18px 20px'}}>
            <div style={{display:'flex',alignItems:'center',marginBottom:12}}>
              <b style={{fontSize:15,color:'#1B3A5C'}}>표시 안내</b>
              <button className="btn btn-outline btn-sm" style={{marginLeft:'auto'}} onClick={function(){setLegendOpen(false)}}>✕ 닫기</button>
            </div>
            <div style={{fontSize:12.5,fontWeight:800,color:'#1B3A5C',margin:'6px 0 6px'}}>상태</div>
            {[['대기','수출업체가 신청했고 아직 수락하지 않은 요청입니다.'],
              ['접수','요청을 수락해 처리 중인 단계입니다.'],
              ['수거완료','번호판·서류를 수거했습니다. 구청 접수 전 단계입니다.'],
              ['진행중','구청에 접수해 말소를 진행하고 있습니다.'],
              ['보류','서류 미비·압류 등으로 잠시 멈춘 상태입니다. 사유가 함께 표시됩니다.'],
              ['완료','말소가 끝난 건입니다. 정산에 자동 반영됩니다.'],
              ['취소','수출업체 요구 등으로 취소된 건입니다.']].map(function(x){
              var lc=statusColor(x[0]);
              return (<div key={x[0]} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'5px 0',fontSize:12.5,lineHeight:1.55}}>
                <span style={{flexShrink:0,width:78}}><span style={{padding:'4px 8px',borderRadius:12,fontSize:11,fontWeight:'bold',background:lc.bg,color:lc.color}}>{x[0]}</span></span>
                <span style={{color:'#64748b'}}>{x[1]}</span></div>);})}
            <div style={{fontSize:12.5,fontWeight:800,color:'#1B3A5C',margin:'14px 0 6px'}}>뱃지</div>
            {(function(){
              var pill=function(t,bg,c,bd){return <span style={{padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:bg,color:c,border:bd||'none'}}>{t}</span>;};
              var items=[
                [pill('긴급','#fee2e2','#991b1b'),'요청자가 긴급으로 신청한 건 — 우선 처리 대상입니다.'],
                [pill('폐차','#fef2f2','#b91c1c'),'폐차말소 건입니다 (수출말소와 구분).'],
                [pill('압류/저당','#fee2e2','#b91c1c'),'차량에 압류·저당이 걸려 있습니다. 해소 전에는 말소가 안 됩니다.'],
                [pill('해소 필요','#fffbeb','#b45309','1px solid #fde68a'),'자격 확인에서 걸림돌(압류·미납 등)이 발견된 건 — 수거는 진행하되 구청 접수 전에 해소해야 합니다.'],
                [pill('가능','#dcfce7','#15803d'),'자격 확인 통과 — 바로 말소 진행이 가능합니다.'],
                [<span className="btn btn-outline btn-sm" style={{fontSize:11,pointerEvents:'none'}}>서류 요청 1</span>,'수출업체에 보강서류를 요청한 상태입니다. 누르면 이력을 볼 수 있습니다.'],
                [<span className="btn btn-outline btn-sm" style={{fontSize:11,pointerEvents:'none',background:'#eff6ff',borderColor:'#93c5fd',color:'#1d4ed8',fontWeight:700}}>서류 제출됨 1</span>,'수출업체가 서류를 제출했습니다 — 확인이 필요합니다.'],
                [pill('D+3','#fee2e2','#991b1b'),'신청 후 경과일입니다. 3일 이상이면 붉게 표시됩니다.']];
              return items.map(function(x,i){
                return (<div key={i} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'5px 0',fontSize:12.5,lineHeight:1.55}}>
                  <span style={{flexShrink:0,width:100}}>{x[0]}</span>
                  <span style={{color:'#64748b'}}>{x[1]}</span></div>);});
            })()}
          </div>
        </div>
      )}
      {tab==='requests' && (<div>
        {/* 모바일은 2행 — 1행 [세그 칩 4개 전폭] / 2행 [검색][새로고침][표시 안내].
            묶음 div 를 display:contents 로 풀어 세 그룹이 같은 flex 줄에서 wrap 하게 한다(데스크탑 렌더 불변). */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:isMobile?'wrap':undefined,gap:isMobile?8:undefined}}>
          <div style={isMobile?{display:'contents'}:{display:'flex',gap:8,alignItems:'center'}}>
            {/* 칩 4개가 좁은 폭에서 찌그러지지 않도록 — 모바일은 전폭 균등 분할(넘치면 그룹만 가로 스크롤) */}
            <div style={{display:'inline-flex',gap:4,background:'#f1f5f9',padding:3,borderRadius:8,maxWidth:'100%',width:isMobile?'100%':undefined,overflowX:isMobile?'auto':undefined}}>
              {SEGMENTS.map(function(sg){var on=seg===sg.key;return(
                <button key={sg.key} onClick={function(){changeSeg(sg.key)}} style={{padding:isMobile?'9px 6px':'5px 12px',minHeight:isMobile?40:undefined,whiteSpace:'nowrap',flex:isMobile?'1 1 0':undefined,border:'none',borderRadius:6,fontSize:13,fontWeight:700,cursor:'pointer',background:on?'#fff':'transparent',color:on?'#1e40af':'#64748b',boxShadow:on?'0 1px 2px rgba(0,0,0,0.08)':'none'}}>
                  {sg.label} {sg.key!=='done'&&<span style={{fontWeight:600,color:on?'#3b82f6':'#94a3b8'}}>{segCounts[sg.key]}</span>}{/* 완료는 무한 누적값이라 배지 미표시 (Jin 8/26) */}
                </button>)})}
            </div>
            {/* 모바일: 검색창이 2행 왼쪽 — 새로고침·표시 안내가 같은 줄 오른쪽에 붙는다 */}
            <div style={{display:'flex',gap:8,alignItems:'center',flex:isMobile?'1 1 120px':undefined,minWidth:isMobile?0:undefined}}>
              <input value={agencySearch} onChange={function(e){setAgencySearch(e.target.value)}} placeholder="업체/차량번호 검색" style={{padding:isMobile?'10px 12px':'6px 12px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:13,width:isMobile?'100%':180,minWidth:isMobile?0:undefined,minHeight:isMobile?40:undefined,boxSizing:isMobile?'border-box':undefined}}/>
              <button className="btn btn-outline btn-sm" style={Object.assign({},isMobile?{whiteSpace:'nowrap',flexShrink:0}:null,tapRowBtn)} onClick={loadAll}>새로고침</button>
            </div>
          </div>
          {/* 화면 우측 제일 끝 — 바깥 행이 space-between 이라 여기가 우측 끝 (Jin 8/26) */}
          <button className="btn btn-outline btn-sm" style={Object.assign({flexShrink:0},isMobile?{whiteSpace:'nowrap'}:null,tapRowBtn)} onClick={function(){setLegendOpen(true)}} title="상태·뱃지 설명">표시 안내</button>
        </div>
        {/* ── 업체 · 월 필터 줄 — 세그먼트/검색과 AND. 모바일은 업체 드롭다운이 남는 폭을 먹고 월 내비가 오른쪽 ── */}
        <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:8,flexWrap:'wrap'}}>
          {/* 업체 = 표의 업체 열과 같은 값(대행사 계정이면 수출업체). 건수는 현 세그먼트 기준 */}
          {/* 고정 폭 — select 내재 폭이 세그먼트별 옵션(업체명·건수)에 따라 달라져 탭마다 크기가 튀던 것 통일 (Jin 8/26).
              닫힘 상태 긴 업체명은 브라우저가 말줄임, 펼친 목록은 전체 표시라 손실 없음. */}
          <select aria-label={isCancelAgency?'수출업체 선택':'대행업체 선택'} value={reqCompany} onChange={function(e){setReqCompany(e.target.value);setSelectedIds([]);}} style={Object.assign({padding:isMobile?'8px 8px':'5px 8px',minHeight:isMobile?40:undefined,border:'1px solid #d0d5dd',borderRadius:6,fontSize:12,fontWeight:600,color:reqCompany?'#1e40af':'#64748b'},isMobile?{flex:'1 1 150px',minWidth:0}:{width:160,flexShrink:0})}>
            <option value="">{isCancelAgency?'수출업체 전체':'대행업체 전체'}</option>
            {reqCompanyOpts.map(function(o){return(<option key={o.name} value={o.name}>{o.name} ({o.count})</option>)})}
          </select>
          {/* 완료·보류 — 정산 현황과 같은 ‹YYYY년 M월› 월 내비 (라벨 클릭 = month input). 라벨 텍스트 없음 (Jin 8/26).
              기준 열은 세그먼트를 따른다(완료=완료일, 보류=요청일 — reqDateFieldFor). */}
          {reqPeriodUsable && (
          <span style={{display:'inline-flex',alignItems:'center',gap:4,flexShrink:0}}>
            <button title="전월" style={reqNavBtnSt} onClick={function(){shiftReqMonth(-1)}}>‹</button>
            {showReqMonthInput
              ? <input type="month" autoFocus value={reqMonth} onBlur={function(){setShowReqMonthInput(false)}}
                  onChange={function(e){if(e.target.value){setReqMonth(e.target.value);setSelectedIds([]);}}}
                  style={{padding:'5px 8px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:13}}/>
              : <button title="월 직접 선택" onClick={function(){setShowReqMonthInput(true)}}
                  style={isMobile
                    ?{background:'none',border:'none',fontSize:13,fontWeight:700,color:'#334155',cursor:'pointer',padding:'0 6px',height:40,whiteSpace:'nowrap'}
                    :{background:'none',border:'none',fontSize:13,fontWeight:700,color:'#334155',cursor:'pointer',padding:'2px 4px',whiteSpace:'nowrap'}}>{reqMonthTitle}</button>}
            <button title="익월" style={reqNavBtnSt} onClick={function(){shiftReqMonth(1)}}>›</button>
          </span>)}
        </div>
        {reqFilterOn && (<div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',background:'#eff6ff',border:'1px solid #bfdbfe',padding:'7px 12px',borderRadius:8,marginBottom:8}}>
          <span style={{fontSize:12,fontWeight:700,color:'#1e40af'}} title={segLabel(seg)+' 세그먼트 기준'}>필터 적용 중 · {filteredReqs.length}건 / 전체 {segReqs.length}건</span>
          <button className="btn btn-outline btn-sm" style={tapBtn} onClick={resetReqFilters}>초기화</button>
        </div>)}
        {showForm && (<div className="card" style={{padding:isMobile?16:20,marginBottom:16,border:'2px solid #3b82f6'}}>
          <h3 style={{margin:'0 0 12px'}}>{editReq?'대행 요청 수정':'말소 대행 등록'}</h3>
          {editReq&&presenceBanner(editReq.id)}
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr',gap:12}}>
            <div><label style={ls}>차량 선택*</label><select style={is} value={fCarId} onChange={function(e){setFCarId(e.target.value)}} disabled={!!editReq}>
              <option value="">-- 선택 --</option>
              {cars.filter(function(c){return c.status==='등록완료'||c.status==='말소신청'||(editReq&&c.id===editReq.carId)}).map(function(c){return(<option key={c.id} value={c.id}>{c.carNo||c.vin} - {c.owner}</option>)})}
            </select></div>
            <div><label style={ls}>대행업체</label><select style={is} value={fAgencyId} onChange={function(e){setFAgencyId(e.target.value)}}>
              <option value="">-- 선택 --</option>
              {agencies.filter(function(a){return a.active}).map(function(a){return(<option key={a.id} value={a.id}>{a.name} ({fmt(a.fee)}원)</option>)})}
            </select></div>
            <div><label style={ls}>수수료 (원)</label><input style={is} type="number" value={fFee} onChange={function(e){setFFee(e.target.value)}} placeholder="0"/></div>
            {/* grid 자식 min-width:auto — date input 기본 최소폭이 셀을 벌려 옆 칸을 민다 (bcf46038) */}
            <div style={{minWidth:0}}><label style={ls}>신청일</label><input style={{...is,minWidth:0,WebkitAppearance:'none'}} type="date" value={fDate} onChange={function(e){setFDate(e.target.value)}}/></div>
            <div style={{gridColumn:isMobile?'auto':'span 2'}}><label style={ls}>메모</label><input style={is} value={fMemo} onChange={function(e){setFMemo(e.target.value)}} placeholder="특이사항"/></div>
          </div>
          {editReq
            ? <RequestServicesEditor requestId={editReq.id} agencyId={fAgencyId} showFee={true} readOnly={false} showToast={showToast} />
            : <ExtraServicePicker agencyId={fAgencyId} value={fServices} onChange={setFServices} showFee={true} />}
          <div style={{display:'flex',gap:8,marginTop:16}}>
            <button className="btn btn-blue" style={tapBtn} onClick={saveRequest}>{editReq?'수정':'등록'}</button>
            <button className="btn btn-outline" style={tapBtn} onClick={clearReqForm}>취소</button>
          </div>
        </div>)}
        {selectedIds.length>0&&(<div style={{background:'#eff6ff',border:'1px solid #93c5fd',padding:'10px 14px',borderRadius:8,marginBottom:8,display:'flex',flexWrap:isMobile?'wrap':undefined,gap:isMobile?8:undefined,justifyContent:'space-between',alignItems:'center'}}>
          <span style={{fontSize:13,fontWeight:700,color:'#1e40af'}}>{selectedIds.length}건 선택됨</span>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-sm" style={Object.assign({background:'#16a34a',color:'#fff',border:'none'},tapBtn)} onClick={bulkAccept}>일괄 수락 (접수)</button>
            <button className="btn btn-outline btn-sm" style={tapBtn} onClick={function(){setSelectedIds([])}}>선택 해제</button>
          </div>
        </div>)}
        {/* 완료 세그먼트만 장부형 목록으로 분기 (Jin 확정 시안 redesign/done-list.html).
            신규/진행중/보류는 아래 표 그대로 — 트리아지 UI(상태 드롭다운/일괄 수락/D+ 배지)가 필요한 쪽이다. */}
        {seg==='done' ? (
          <CancelAgencyDoneList
            rows={filteredReqs} isStaff={isStaff} isMobile={isMobile} docList={docList}
            nameOf={reqCompanyName} sortKey={reqSortKey} sortTh={reqSortTh} periodLabel={reqPeriodLabel}
            presenceBadge={reqPresenceBadge}
            onOpenDocs={openDocSheet} onRevert={revertDone} onEdit={startEditReq} onRowClick={openDetail} showToast={showToast} />
        ) : isMobile ? (
        /* 모바일(≤768) 카드 목록 — 표는 390px 에서 3열만 보인다(실측 가로 스크롤 940 / 가시 316).
           그룹 구분·체크박스·상태 드롭다운·서류 배지·⋯ 메뉴는 표와 같은 조각(reqGroupBar/reqStatusSelect/…)을 쓴다. */
        <div>
          {filteredReqs.length>0&&(<label style={{display:'flex',alignItems:'center',gap:8,minHeight:40,padding:'0 2px',marginBottom:2,fontSize:12,fontWeight:700,color:'#475569',cursor:'pointer'}}>
            <input type="checkbox" style={{width:20,height:20}} checked={selectedIds.length===filteredReqs.length} onChange={function(){toggleSelectAll(filteredReqs)}}/>
            전체 선택 <span style={{fontWeight:600,color:'#94a3b8'}}>{filteredReqs.length}건</span>
          </label>)}
          {filteredReqs.map(function(r,ri){var sc=statusColor(r.status);var isHi=highlightId===r.id;var checked=selectedIds.indexOf(r.id)>=0;
            var dKey=reqDateKey(r);var showDateSep=!reqSortKey&&(ri===0||dKey!==reqDateKey(filteredReqs[ri-1]));
            var comp=reqCompanyName(r);var showGroupSep=!reqSortKey&&(showDateSep||comp!==reqCompanyName(filteredReqs[ri-1]));
            var gRows=showGroupSep?reqGroupRows(filteredReqs,ri):null;
            var ag=r.status==='완료'?null:reqAgeDays(r.requestDate);
            return(
            <React.Fragment key={r.id}>
              {showDateSep&&(<div style={{padding:'10px 2px 4px',fontSize:11,fontWeight:700,color:'#64748b'}}>{reqDateLabel(dKey)}</div>)}
              {showGroupSep&&(<div style={{background:'#f8fafc',border:'1px solid #eef2f7',borderRadius:8,padding:'8px 10px',marginBottom:6}}>{reqGroupBar(dKey,comp,gRows)}</div>)}
              <div id={'cancel-req-row-'+r.id} onClick={function(){openDetail(r)}} style={Object.assign({cursor:'pointer'},mReqCard,isHi?{background:'#fef3c7',borderColor:'#f59e0b'}:(checked?{background:'#eff6ff',borderColor:'#93c5fd'}:null))}>
                <div style={{display:'flex',gap:10,alignItems:'flex-start'}}>
                  <input type="checkbox" style={{width:20,height:20,marginTop:3,flexShrink:0}} checked={checked} onClick={function(e){e.stopPropagation()}} onChange={function(){toggleSelect(r.id)}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={mCardRow}>
                      <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',rowGap:4,flex:1,minWidth:0}}>
                        {reqPresenceBadge(r.id)}<span style={{fontSize:15,fontWeight:800,color:'#0f172a'}}>{r.carNo||'-'}</span>{reqBadges(r)}
                      </div>
                      <span style={Object.assign({padding:'3px 8px',borderRadius:12,fontSize:11,fontWeight:'bold',background:sc.bg,color:sc.color},mCardR)}>{statusLabel(r.status,r.cancelType)}</span>
                    </div>
                    <div style={Object.assign({},mCardRow,{marginTop:3})}>
                      <span style={Object.assign({fontSize:12,color:'#64748b'},mCardL)}>{[r.carType,r.owner].filter(Boolean).join(' · ')||'-'}</span>
                      {!isStaff&&<span style={Object.assign({fontSize:12,fontWeight:700,color:'#334155'},mCardR)}>{fmt(r.fee)} 원</span>}
                    </div>
                    <div style={Object.assign({},mCardRow,{marginTop:1})}>
                      <span style={Object.assign({fontFamily:'monospace',fontSize:11,color:'#94a3b8'},mCardL)}>{r.vin||'-'}</span>
                      <span style={Object.assign({fontSize:11,color:'#94a3b8'},mCardR)}>
                        신청 {r.requestDate||'-'}
                        {ag!=null&&ag>=1&&<span style={{marginLeft:5,fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:8,background:ag>=3?'#fee2e2':'#f1f5f9',color:ag>=3?'#991b1b':'#475569'}} title="신청 후 경과일">D+{ag}</span>}
                      </span>
                    </div>
                    <div style={Object.assign({},mCardRow,{marginTop:5})}>
                      <span style={Object.assign({fontSize:12,color:'#475569'},mCardL)}>{reqCompanyName(r)}{' · 담당 '+(r.staffName||'-')}</span>
                      {!!r.completedDate&&<span style={Object.assign({fontSize:11,color:'#94a3b8'},mCardR)}>완료 {r.completedDate}</span>}
                    </div>
                  </div>
                </div>
                <div onClick={function(e){e.stopPropagation()}} style={{display:'flex',alignItems:'center',gap:6,marginTop:9,paddingTop:8,borderTop:'1px solid #f1f5f9'}}>
                  {reqStatusSelect(r,{marginRight:0,flex:'1 1 0',minWidth:0})}
                  {reqDocBtn(r,{marginRight:0,flexShrink:0})}
                  {reqMoreMenu(r,{flexShrink:0})}
                </div>
              </div>
            </React.Fragment>)})}
          {filteredReqs.length===0&&(<div className="card" style={{padding:32,textAlign:'center',color:'#94a3b8'}}>말소 대행 요청이 없습니다.</div>)}
        </div>
        ) : (
        <div className="card" style={isMobile?{padding:12}:undefined}>
          <div style={isMobile?hScroll:undefined}>
          <table className="data-table sticky-head" style={isMobile?{minWidth:760}:undefined}>
            <thead><tr>
              <th style={{width:36}}><input type="checkbox" style={isMobile?{width:20,height:20}:undefined} checked={filteredReqs.length>0&&selectedIds.length===filteredReqs.length} onChange={function(){toggleSelectAll(filteredReqs)}}/></th>
              {reqSortTh('차량','carNo')}<th>차대번호</th><th>소유자</th>
              {reqSortTh(isCancelAgency?'수출업체':'대행업체','company')}
              <th>상태</th><th style={{width:60}}>담당</th>{!isStaff&&<th>수수료</th>}{reqSortTh('신청일','requestDate')}{reqSortTh('완료일','completedDate')}<th>관리</th>
            </tr></thead>
            <tbody>
              {filteredReqs.map(function(r,ri){var sc=statusColor(r.status);var isHi=highlightId===r.id;var checked=selectedIds.indexOf(r.id)>=0;
                // 헤더 정렬을 고른 동안에는 (날짜 × 업체) 구분 행을 접는다 — 예: 차량번호 순이면
                // 날짜가 매 행 바뀌어 구분 행이 목록보다 많아진다. 기본 정렬로 돌아오면 다시 나온다.
                var dKey=reqDateKey(r);var showDateSep=!reqSortKey&&(ri===0||dKey!==reqDateKey(filteredReqs[ri-1]));
                var comp=reqCompanyName(r);var showGroupSep=!reqSortKey&&(showDateSep||comp!==reqCompanyName(filteredReqs[ri-1]));
                var gRows=showGroupSep?reqGroupRows(filteredReqs,ri):null;
                // #cap-web: 동시 작업 프레즌스 — 차량번호 앞 점 (띠는 2026-09-10 폐기)
                return(
                <React.Fragment key={r.id}>
                {showDateSep&&(<tr><td colSpan={isStaff?11:12} style={{padding:'6px 10px',background:'#f8fafc',borderTop:'1px solid #e2e8f0',fontSize:11,fontWeight:700,color:'#64748b'}}>{reqDateLabel(dKey)}</td></tr>)}
                {showGroupSep&&(<tr><td colSpan={isStaff?11:12} style={{padding:'5px 10px 5px 28px',background:'#fdfefe',borderBottom:'1px solid #eef2f7'}}>
                  {reqGroupBar(dKey,comp,gRows)}
                </td></tr>)}
                <tr id={'cancel-req-row-'+r.id} onClick={function(){openDetail(r)}} style={Object.assign({cursor:'pointer'},isHi?{background:'#fef3c7',transition:'background 0.5s',outline:'2px solid #f59e0b'}:(checked?{background:'#eff6ff'}:undefined))}>
                  <td style={{textAlign:'center'}} onClick={function(e){e.stopPropagation()}}><input type="checkbox" style={isMobile?{width:20,height:20}:undefined} checked={checked} onChange={function(){toggleSelect(r.id)}}/></td>
                  <td style={{fontWeight:700}}>{reqPresenceBadge(r.id)}{r.carNo||'-'}{reqBadges(r)}{r.carType&&<span style={{marginLeft:7,fontWeight:400,color:'#64748b',fontSize:12}}>{r.carType}</span>}</td>
                  <td style={{fontFamily:'monospace',fontSize:11,color:'#666'}}>{r.vin||'-'}</td>
                  <td>{r.owner||'-'}</td>
                  <td>{(isCancelAgency?r.requesterCompanyName:r.agencyName)||<span style={{color:'#94a3b8'}}>미지정</span>}</td>
                  <td><span style={{padding:'4px 8px',borderRadius:12,fontSize:11,fontWeight:'bold',background:sc.bg,color:sc.color}}>{statusLabel(r.status,r.cancelType)}</span></td>
                  <td style={{fontSize:12,color:'#475569'}}>{r.staffName||'-'}</td>
                  {!isStaff&&<td style={{textAlign:'right'}}>{fmt(r.fee)} 원</td>}
                  <td>{r.requestDate||'-'}{(function(){if(r.status==='완료')return null;var ag=reqAgeDays(r.requestDate);if(ag==null||ag<1)return null;return <span style={{marginLeft:6,fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:8,background:ag>=3?'#fee2e2':'#f1f5f9',color:ag>=3?'#991b1b':'#475569'}} title="신청 후 경과일">D+{ag}</span>;})()}</td>
                  <td>{r.completedDate||'-'}</td>
                  <td style={isMobile?{whiteSpace:'nowrap'}:undefined} onClick={function(e){e.stopPropagation()}}>
                    {reqStatusSelect(r)}
                    {reqDocBtn(r)}
                    {reqMoreMenu(r)}
                  </td>
                </tr>
                </React.Fragment>)})}
              {filteredReqs.length===0&&(<tr><td colSpan={isStaff?11:12} style={{padding:40,textAlign:'center',color:'#94a3b8'}}>말소 대행 요청이 없습니다.</td></tr>)}
            </tbody>
          </table>
          </div>
        </div>
        )}
      </div>)}

      {/* ── 폐차 탭 (① 수출업체 폐차말소 신청 + ② walk-in 폐차 대행) ── */}
      {tab==='scrap' && (<div>
        {/* ① 수출업체가 신청한 폐차말소 (cancel_requests cancelType='폐차말소') — Kyle 2026-06-19. requests state 재사용(loadAll). */}
        {(function(){var scrapCancels=requests.filter(function(r){return r.cancelType==='폐차말소'});return(
        <div style={{marginBottom:24}}>
          <div style={{fontSize:14,fontWeight:700,color:'#1e3a5f',marginBottom:8}}>수출업체 폐차말소 신청 <span style={{fontSize:12,fontWeight:400,color:'#64748b'}}>({scrapCancels.length}건)</span></div>
          {isMobile ? (
          /* 모바일 카드 — 7열 표는 390px 에서 상태·관리가 화면 밖(실측 720/340) */
          <div>
            {scrapCancels.map(function(r){var sc=statusColor(r.status);return(
              <div key={r.id} style={mReqCard}>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontSize:15,fontWeight:800,color:'#0f172a'}}>{r.carNo||'-'}</span>
                  {r.lien_status==='has_lien'&&<span style={{padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:'#fee2e2',color:'#b91c1c'}} title={r.lien_detail||'압류/저당'}>압류/저당</span>}
                  <span style={{marginLeft:'auto',flexShrink:0,padding:'3px 8px',borderRadius:12,fontSize:11,fontWeight:'bold',background:sc.bg,color:sc.color}}>{statusLabel(r.status,r.cancelType)}</span>
                </div>
                <div style={Object.assign({},mCardRow,{marginTop:3})}>
                  <span style={Object.assign({fontSize:12,color:'#64748b'},mCardL)}>{[r.carType,r.owner].filter(Boolean).join(' · ')||'-'}</span>
                  <span style={Object.assign({fontSize:11,color:'#94a3b8'},mCardR)}>신청 {r.requestDate||'-'}</span>
                </div>
                <div style={Object.assign({fontSize:11,color:'#94a3b8',marginTop:2},mEllip)}>{(isCancelAgency?r.requesterCompanyName:r.agencyName)||'미지정'}</div>
                <div style={{display:'flex',alignItems:'center',gap:6,marginTop:9,paddingTop:8,borderTop:'1px solid #f1f5f9'}}>
                  {reqStatusSelect(r,{marginRight:0,flex:'1 1 0',minWidth:0})}
                  <button className="btn btn-outline btn-sm" onClick={function(){openDocReq(r.carId,r.carNo,r.cancelType,r.lien_status)}} title="보강서류 요청" style={Object.assign({flexShrink:0},tapRowBtn)}>서류</button>
                  {!isStaff&&<button className="btn btn-outline btn-sm btn-danger" style={Object.assign({flexShrink:0},tapRowBtn)} onClick={function(){deleteRequest(r.id)}}>삭제</button>}
                </div>
              </div>)})}
            {scrapCancels.length===0&&(<div className="card" style={{padding:28,textAlign:'center',color:'#94a3b8'}}>수출업체 폐차말소 신청이 없습니다.</div>)}
          </div>
          ) : (
          <div className="card" style={isMobile?{padding:12}:undefined}>
            <div style={isMobile?hScroll:undefined}>
            <table className="data-table sticky-head" style={isMobile?{minWidth:720}:undefined}>
              <thead><tr><th>차량번호</th><th>차종</th><th>소유자</th><th>{isCancelAgency?'수출업체':'대행업체'}</th><th>상태</th><th>신청일</th><th>관리</th></tr></thead>
              <tbody>
                {scrapCancels.map(function(r){var sc=statusColor(r.status);return(
                  <tr key={r.id}>
                    <td style={{fontWeight:700}}>{r.carNo||'-'}{r.lien_status==='has_lien'&&<span style={{marginLeft:4,padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:700,background:'#fee2e2',color:'#b91c1c'}} title={r.lien_detail||'압류/저당'}>압류/저당</span>}</td>
                    <td>{r.carType||'-'}</td>
                    <td>{r.owner||'-'}</td>
                    <td>{(isCancelAgency?r.requesterCompanyName:r.agencyName)||<span style={{color:'#94a3b8'}}>미지정</span>}</td>
                    <td><span style={{padding:'4px 8px',borderRadius:12,fontSize:11,fontWeight:'bold',background:sc.bg,color:sc.color}}>{statusLabel(r.status,r.cancelType)}</span></td>
                    <td>{r.requestDate||'-'}</td>
                    <td style={isMobile?{whiteSpace:'nowrap'}:undefined}>
                      {reqStatusSelect(r)}
                      <button className="btn btn-outline btn-sm" onClick={function(){openDocReq(r.carId,r.carNo,r.cancelType,r.lien_status)}} title="보강서류 요청" style={Object.assign({marginRight:4},tapRowBtn)}>서류</button>
                      {!isStaff&&<button className="btn btn-outline btn-sm btn-danger" style={tapRowBtn} onClick={function(){deleteRequest(r.id)}}>삭제</button>}
                    </td>
                  </tr>)})}
                {scrapCancels.length===0&&(<tr><td colSpan={7} style={{padding:30,textAlign:'center',color:'#94a3b8'}}>수출업체 폐차말소 신청이 없습니다.</td></tr>)}
              </tbody>
            </table>
            </div>
          </div>
          )}
        </div>);})()}
        {/* ② walk-in 폐차 대행 (차량 무관, 건별 일회성 정산) */}
        <div style={{display:'flex',flexWrap:isMobile?'wrap':undefined,justifyContent:'space-between',alignItems:'center',gap:isMobile?10:undefined,marginBottom:12,borderTop:'1px solid #e2e8f0',paddingTop:16}}>
          <div style={{fontSize:13,color:'#64748b'}}>walk-in 폐차 대행 — 차량(수출차)과 무관한 폐차 의뢰 · 건별 청구</div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-outline btn-sm" style={tapRowBtn} onClick={loadScrap}>새로고침</button>
            <button className="btn btn-green btn-sm" style={tapBtn} onClick={function(){clearScrapForm();setShowScrapForm(true)}}>폐차 접수</button>
          </div>
        </div>
        {showScrapForm && (<div className="card" style={{padding:isMobile?16:20,marginBottom:16,border:'2px solid #3b82f6'}}>
          <h3 style={{margin:'0 0 12px'}}>{editScrap?'폐차 요청 수정':'폐차 접수'}</h3>
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr',gap:12,marginBottom:10}}>
            <div><label style={ls}>의뢰인</label><input style={is} value={scrapForm.requesterName} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{requesterName:e.target.value}))}} placeholder="이름/상호"/></div>
            <div><label style={ls}>연락처</label><input style={is} value={scrapForm.requesterContact} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{requesterContact:e.target.value}))}} placeholder="전화"/></div>
            <div><label style={ls}>거래처(선택)</label><select style={is} value={scrapForm.clientId} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{clientId:e.target.value}))}}>
              <option value="">직접(walk-in)</option>
              {scrapClients.map(function(c){return(<option key={c.id} value={c.id}>{c.name}</option>)})}
            </select></div>
            <div><label style={ls}>차량번호</label><input style={is} value={scrapForm.carNo} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{carNo:e.target.value}))}} placeholder="예: 12가3456"/></div>
            <div><label style={ls}>차종</label><input style={is} value={scrapForm.carType} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{carType:e.target.value}))}}/></div>
            <div><label style={ls}>폐차유형</label><select style={is} value={scrapForm.scrapType} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{scrapType:e.target.value}))}}>
              {SCRAP_TYPES.map(function(t){return(<option key={t} value={t}>{t}</option>)})}
            </select></div>
          </div>
          {/* 금액 8칸 — 모바일은 2열(라벨 짧아 2열이 읽힌다), 부가서비스 메모만 전폭 */}
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'1fr 1fr 1fr 1fr',gap:8,marginBottom:10}}>
            <div><label style={ls}>등록세</label><input type="number" style={is} value={scrapForm.registrationTax} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{registrationTax:e.target.value}))}}/></div>
            <div><label style={ls}>증지</label><input type="number" style={is} value={scrapForm.stampDuty} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{stampDuty:e.target.value}))}}/></div>
            <div><label style={ls}>원부</label><input type="number" style={is} value={scrapForm.wonbuFee} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{wonbuFee:e.target.value}))}}/></div>
            <div><label style={ls}>수수료</label><input type="number" style={is} value={scrapForm.serviceFee} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{serviceFee:e.target.value}))}}/></div>
            <div><label style={ls}>재교부</label><input type="number" style={is} value={scrapForm.reissueFee} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{reissueFee:e.target.value}))}}/></div>
            <div><label style={ls}>기타</label><input type="number" style={is} value={scrapForm.otherFee} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{otherFee:e.target.value}))}}/></div>
            <div><label style={ls}>부가서비스</label><input type="number" style={is} value={scrapForm.extraServiceFee} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{extraServiceFee:e.target.value}))}}/></div>
            <div style={isMobile?{gridColumn:'span 2'}:undefined}><label style={ls}>부가서비스 메모</label><PresetCombo presets={EXTRA_SERVICE_PRESETS} is={is} placeholder="예: 임시번호판 발급" value={scrapForm.extraServiceNote} onChange={function(v){setScrapForm(Object.assign({},scrapForm,{extraServiceNote:v}))}}/></div>
          </div>
          <div style={{display:'flex',flexWrap:isMobile?'wrap':undefined,alignItems:'center',gap:isMobile?10:14,marginBottom:10}}>
            <label style={{fontSize:12,display:'flex',alignItems:'center',gap:6,cursor:'pointer',minHeight:isMobile?40:undefined}}><input type="checkbox" style={isMobile?{width:20,height:20}:undefined} checked={scrapForm.issueTaxInvoice} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{issueTaxInvoice:e.target.checked}))}}/>세금계산서 발행(세액 가산)</label>
            {(function(){var pv=calcSettlementPreview(scrapForm);return(<div style={{flex:1,flexBasis:isMobile?'100%':undefined,padding:'6px 10px',background:'#f0f4f8',borderRadius:6,fontSize:12,color:'#334155',display:'flex',justifyContent:'space-between'}}><span>세액 <b>{(scrapForm.issueTaxInvoice?pv.vat:0).toLocaleString()}</b></span><span>소계 <b style={{color:'#059669'}}>{(scrapForm.issueTaxInvoice?pv.subtotal:pv.subtotal-pv.vat).toLocaleString()}원</b></span></div>)})()}
          </div>
          <div style={{marginBottom:10}}><label style={ls}>메모</label><input style={is} value={scrapForm.memo} onChange={function(e){setScrapForm(Object.assign({},scrapForm,{memo:e.target.value}))}} placeholder="특이사항"/></div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-blue" style={tapBtn} onClick={saveScrap}>{editScrap?'수정':'접수'}</button>
            <button className="btn btn-outline" style={tapBtn} onClick={clearScrapForm}>취소</button>
          </div>
        </div>)}
        {isMobile ? (
        /* 모바일 카드 — 10열 표는 390px 에서 상태·소계·관리가 화면 밖(실측 900/340) */
        <div>
          {scrapList.map(function(s){var sc=statusColor(s.status);return(
            <div key={s.id} style={mReqCard}>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <span style={Object.assign({fontSize:14,fontWeight:800,color:'#0f172a',minWidth:0},mEllip)}>{s.requesterName||'-'}</span>
                <span style={{marginLeft:'auto',flexShrink:0,padding:'3px 8px',borderRadius:12,fontSize:11,fontWeight:'bold',background:sc.bg,color:sc.color}}>{s.status}</span>
              </div>
              <div style={Object.assign({},mCardRow,{marginTop:3})}>
                <span style={Object.assign({fontSize:12,color:'#64748b'},mCardL)}>{[s.carNo,s.carType].filter(Boolean).join(' · ')||'-'}</span>
                <span style={Object.assign({fontSize:11,color:'#94a3b8'},mCardR)}>접수 {s.acceptedAt||s.requestDate||'-'}</span>
              </div>
              <div style={Object.assign({},mCardRow,{marginTop:4})}>
                <span style={Object.assign({fontSize:11,color:'#94a3b8'},mCardL)}>{s.scrapType||'-'} · {s.clientName||'walk-in'} · {s.requesterContact||'-'}</span>
                <span style={Object.assign({fontSize:12,fontWeight:700,color:'#334155'},mCardR)}>{fmt(s.subtotal)} 원</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:6,marginTop:9,paddingTop:8,borderTop:'1px solid #f1f5f9'}}>
                {scrapStatusSelect(s,{marginRight:0,flex:'1 1 0',minWidth:0})}
                <button className="btn btn-outline btn-sm" onClick={function(){openPdf('/scrap-requests/'+s.id+'/receipt-pdf')}} style={Object.assign({flexShrink:0},tapRowBtn)}>접수증</button>
                <button className="btn btn-outline btn-sm" onClick={function(){startEditScrap(s)}} style={Object.assign({flexShrink:0},tapRowBtn)}>수정</button>
                <button className="btn btn-outline btn-sm btn-danger" style={Object.assign({flexShrink:0},tapRowBtn)} onClick={function(){deleteScrap(s.id)}}>삭제</button>
              </div>
            </div>)})}
          {scrapList.length===0&&(<div className="card" style={{padding:32,textAlign:'center',color:'#94a3b8'}}>폐차 요청이 없습니다.</div>)}
        </div>
        ) : (
        <div className="card" style={isMobile?{padding:12}:undefined}>
          <div style={isMobile?hScroll:undefined}>
          <table className="data-table sticky-head" style={isMobile?{minWidth:900}:undefined}>
            <thead><tr>
              <th>의뢰인</th><th>연락처</th><th>차량번호</th><th>차종</th><th>폐차유형</th><th>거래처</th><th>상태</th><th>소계</th><th>접수일</th><th>관리</th>
            </tr></thead>
            <tbody>
              {scrapList.map(function(s){var sc=statusColor(s.status);return(
                <tr key={s.id}>
                  <td style={{fontWeight:700}}>{s.requesterName||'-'}</td>
                  <td>{s.requesterContact||'-'}</td>
                  <td>{s.carNo||'-'}</td>
                  <td>{s.carType||'-'}</td>
                  <td>{s.scrapType||'-'}</td>
                  <td>{s.clientName||<span style={{color:'#94a3b8'}}>walk-in</span>}</td>
                  <td><span style={{padding:'4px 8px',borderRadius:12,fontSize:11,fontWeight:'bold',background:sc.bg,color:sc.color}}>{s.status}</span></td>
                  <td style={{textAlign:'right'}}>{fmt(s.subtotal)} 원</td>
                  <td>{s.acceptedAt||s.requestDate||'-'}</td>
                  <td style={isMobile?{whiteSpace:'nowrap'}:undefined}>
                    {scrapStatusSelect(s)}
                    <button className="btn btn-outline btn-sm" onClick={function(){openPdf('/scrap-requests/'+s.id+'/receipt-pdf')}} style={Object.assign({marginRight:4},tapRowBtn)}>접수증</button>
                    <button className="btn btn-outline btn-sm" onClick={function(){startEditScrap(s)}} style={Object.assign({marginRight:4},tapRowBtn)}>수정</button>
                    <button className="btn btn-outline btn-sm btn-danger" style={tapRowBtn} onClick={function(){deleteScrap(s.id)}}>삭제</button>
                  </td>
                </tr>)})}
              {scrapList.length===0&&(<tr><td colSpan={10} style={{padding:40,textAlign:'center',color:'#94a3b8'}}>폐차 요청이 없습니다.</td></tr>)}
            </tbody>
          </table>
          </div>
        </div>
        )}
      </div>)}

      {/* ── 보강서류 시트 — 대행요청 행의 배지로 열린다 (구 '보강서류' 탭 흡수, 2026-08-26) ── */}
      {docSheet && <CancelAgencyDocReqSheet
        carNo={docSheet.carNo}
        presenceBanner={presenceBanner(docSheet.reqId)}
        items={docList.filter(function(it){return parseInt(it.carId,10)===parseInt(docSheet.carId,10)})}
        isMobile={isMobile}
        showToast={showToast}
        onClose={function(){setDocSheet(null)}}
        onRefresh={loadDocList}
        onNew={function(){openDocReq(docSheet.carId,docSheet.carNo,docSheet.cancelType,docSheet.lienStatus)}}
        onComplete={completeDocReq}
        onCancelReq={cancelDocReq}
        onOpenFile={function(reqId,fileId){openPdf('/cancel-doc-requests/'+reqId+'/files/'+fileId+'/download')}}
      />}

      {/* 보강서류 요청 발행 모달 */}
      {docReqModal&&<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}} onClick={function(){setDocReqModal(null)}}>
        <div style={{background:'#fff',borderRadius:12,padding:isMobile?18:24,width:420,maxWidth:'94vw',maxHeight:'90vh',overflowY:'auto'}} onClick={function(e){e.stopPropagation()}}>
          <h3 style={{margin:'0 0 4px'}}>보강서류 요청</h3>
          <div style={{fontSize:13,color:'#64748b',marginBottom:14}}>{docReqModal.carNo} — 수출업체에 필요 서류를 요청합니다.</div>
          <div style={{fontSize:12,fontWeight:600,color:'#374151',marginBottom:6}}>필요 서류 (복수 선택) <span style={{fontWeight:400,color:'#16a34a',fontSize:11}}>추천 자동선택됨 — 수정 가능</span></div>
          <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:14}}>
            {DOC_CHECKLIST.map(function(d){var on=docChecks.indexOf(d)>=0;return <button key={d} onClick={function(){toggleDocCheck(d)}} style={{padding:isMobile?'10px 12px':'6px 10px',minHeight:isMobile?40:undefined,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',border:'1px solid '+(on?'#2563eb':'#d0d5dd'),background:on?'#eff6ff':'#fff',color:on?'#1d4ed8':'#475569'}}>{on?'✓ ':''}{d}</button>})}
          </div>
          <div style={{fontSize:12,fontWeight:600,color:'#374151',marginBottom:4}}>메모 (선택)</div>
          <textarea value={docMemo} onChange={function(e){setDocMemo(e.target.value)}} placeholder="추가 안내 (예: 인감증명서는 3개월 이내 발급분)" style={{width:'100%',boxSizing:'border-box',minHeight:60,padding:'8px 10px',border:'1px solid #d0d5dd',borderRadius:8,fontSize:13,outline:'none',marginBottom:16}}/>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-blue" style={Object.assign({flex:1},tapBtn)} onClick={submitDocReq}>요청 전송</button>
            <button className="btn btn-outline" style={Object.assign({flex:1},tapBtn)} onClick={function(){setDocReqModal(null)}}>취소</button>
          </div>
        </div>
      </div>}

      {/* ── 대량 말소증 (말소대행 세그먼트 4번째) — 화면은 기존 BulkCancelUploadPage 그대로 ── */}
      {tab==='bulk' && <BulkCancelUploadPage showToast={showToast} user={user}/>}

      {/* ── 대행업체 탭 ── */}
      {tab==='agencies' && (<div>
        {/* 내 대행사 정보 + 계정 설정 통합 (관리 메뉴 항목 1개로 — Jin 8/12). 관리자 갈래는 세그먼트 없음 */}
        {/* 내 대행사 정보/계정 설정 세그는 헤더 슬롯 포털로 이동 (Jin 8/26 통일) */}
        {/* 계정 설정 — '내 대행사 정보' 카드와 같은 문법(파란 테두리 카드 + sec() 구분선, Jin 8/26 통일).
            공용 SettingsPage 대신 두 섹션만 flat 임베드 — 타 계정 설정 화면은 무변경. */}
        {isCancelAgency && agencyView==='account' && (
          <div className="card ca-acct-embed" style={{padding:isMobile?16:20,marginBottom:16,border:'2px solid #3b82f6',maxWidth:FORM_MAX_W,marginLeft:'auto',marginRight:'auto'}}>
            <h3 style={{margin:'0 0 12px'}}>계정 설정</h3>
            {sec('비밀번호 변경')}
            <div style={{marginTop:10}}><PasswordChangeSection showToast={showToast} flat/></div>
            {sec('내 데이터')}
            <div style={{marginTop:10}}><DataManageSection showToast={showToast} user={user} flat/></div>
          </div>
        )}
        {(!isCancelAgency || agencyView==='info') && <div>
        {!isCancelAgency && <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
          <button className="btn btn-green" style={tapBtn} onClick={function(){clearAgencyForm();setShowAgencyForm(true)}}>업체 등록</button>
        </div>}
        {isCancelAgency && !myAgency && <div className="card" style={{padding:40,textAlign:'center',color:'#94a3b8',maxWidth:FORM_MAX_W,marginLeft:'auto',marginRight:'auto'}}>대행사 정보를 불러오는 중입니다.</div>}
        {/* 폼 최대폭 + 중앙 정렬 — 넓은 모니터에서 3열이 그대로 늘어나 열 하나가 700px 를 넘고(2258px 창에서
            실측 717px) 11px 라벨이 빈 여백에 떠 가독성이 무너졌다. 1200px 상한이면 열당 ~378px.
            대행사 갈래는 이 폼이 화면의 전부라 가운데로 모은다(세그먼트 버튼 줄도 같은 폭·정렬 → 왼쪽 끝 일치).
            관리자 갈래는 아래에 전폭 목록 표가 있어 폼만 가운데로 띄우면 표와 어긋나므로 왼쪽 정렬 유지.
            표 자체는 폭이 넓을수록 유리하므로 카드 밖에 있고 상한을 두지 않는다. */}
        {showAgencyForm && (<div className="card" style={{padding:isMobile?16:20,marginBottom:16,border:'2px solid #3b82f6',position:'relative',maxWidth:FORM_MAX_W,marginLeft:isCancelAgency?'auto':undefined,marginRight:isCancelAgency?'auto':undefined}}>
          {!isCancelAgency && <button onClick={clearAgencyForm} style={{position:'absolute',top:12,right:12,background:'none',border:'none',fontSize:18,cursor:'pointer',color:'#94a3b8',lineHeight:1}} onMouseEnter={function(e){e.target.style.color='#dc2626'}} onMouseLeave={function(e){e.target.style.color='#94a3b8'}}>✕</button>}
          <h3 style={{margin:'0 0 12px'}}>{isCancelAgency?'내 대행사 정보':(editAgency?'업체 수정':'업체 등록')}</h3>
          {!editAgency&&<div style={{marginBottom:14}}>
            <div style={{border:'2px dashed #d0d5dd',borderRadius:8,padding:aOcrPreview?8:20,textAlign:'center',cursor:'pointer',background:'#f8fafc',position:'relative',overflow:'hidden',transition:'all .15s'}}
              onClick={function(){document.getElementById('agency-biz-input').click()}}
              onDragOver={function(e){e.preventDefault();e.currentTarget.style.background='#eff6ff';e.currentTarget.style.borderColor='#3b82f6'}}
              onDragLeave={function(e){e.currentTarget.style.background='#f8fafc';e.currentTarget.style.borderColor='#d0d5dd'}}
              onDrop={function(e){e.preventDefault();e.currentTarget.style.background='#f8fafc';e.currentTarget.style.borderColor='#d0d5dd';if(e.dataTransfer.files[0])handleAgencyBizLicense(e.dataTransfer.files[0])}}>
              {aOcrLoading&&<div style={{position:'absolute',top:0,left:0,right:0,bottom:0,background:'rgba(255,255,255,0.9)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',zIndex:2}}>
                <div style={{width:28,height:28,border:'3px solid #e5e7eb',borderTopColor:'#1B3A5C',borderRadius:'50%',animation:'spin 1s linear infinite'}}></div>
                <div style={{marginTop:8,fontSize:12,color:'#1B3A5C',fontWeight:600}}>사업자등록증 분석 중...</div>
              </div>}
              {aOcrPreview?<img src={aOcrPreview} alt="사업자등록증" style={{maxWidth:'100%',maxHeight:150,display:'block',margin:'0 auto'}}/>:
              <div><div style={{fontSize:12,fontWeight:600,color:'#374151'}}>사업자등록증 업로드 (자동 정보 입력)</div><div style={{fontSize:10,color:'#94a3b8',marginTop:4}}>클릭 또는 드래그</div></div>}
            </div>
            <input id="agency-biz-input" type="file" accept="image/*" style={{display:'none'}} onChange={function(e){if(e.target.files[0])handleAgencyBizLicense(e.target.files[0])}}/>
          </div>}
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr',gap:12}}>
            {sec('기본 정보')}
            <div><label style={ls}>업체명*</label><input style={is} value={aName} onChange={function(e){setAName(e.target.value)}}/></div>
            <div><label style={ls}>사업자번호</label><input style={is} value={aBizNo} onChange={function(e){setABizNo(e.target.value)}}/></div>
            <div><label style={ls}>대표자</label><input style={is} value={aCeo} onChange={function(e){setACeo(e.target.value)}}/></div>
            {sec('연락처')}
            <div><label style={ls}>주소</label><input style={is} value={aAddr} onChange={function(e){setAAddr(e.target.value)}}/></div>
            <div><label style={ls}>전화</label><input style={is} value={aTel} onChange={function(e){setATel(e.target.value)}}/></div>
            <div><label style={ls}>이메일</label><input style={is} value={aEmail} onChange={function(e){setAEmail(e.target.value)}}/></div>
            {sec('정산')}
            <div style={wide}><label style={ls}>계좌정보</label><input style={is} value={aBankInfo} onChange={function(e){setABankInfo(e.target.value)}} placeholder="은행 계좌번호 예금주"/><small style={cs}>정산서에 인쇄되는 입금 계좌입니다.</small></div>
            {/* 정산 폴백 단가 — PUT 전용(POST 는 서버가 안 받음) 이라 편집 중일 때만 노출. 대행사 갈래는 항상 editAgency 가 채워져 늘 보인다.
                공통 설명은 그룹 노트로 한 번만 두고, 칸별 캡션은 서로 다른 조건(비어 있음 / 0)만 남긴다. */}
            {!!editAgency && <div style={noteSt}>거래처마다 입력해 둔 금액이 <b>항상 먼저</b> 적용됩니다. 아래 두 값은 <b>거래처 쪽을 비워 둔 경우에만</b> 대신 쓰이므로, 이미 입력해 둔 거래처에는 영향이 없습니다.</div>}
            {!!editAgency && <div><label style={ls}>정산 기본 수수료 (원)</label><input style={is} type="number" min="0" step="1" value={aServiceFeeDefault} onChange={function(e){setAServiceFeeDefault(e.target.value)}} placeholder="비우면 변경 안 함"/><small style={cs}>거래처 수수료를 <b>비워 두면</b> 이 금액이 적용됩니다</small></div>}
            {!!editAgency && <div><label style={ls}>정산 기본 재교부료 (원)</label><input style={is} type="number" min="0" step="1" value={aReissueFeeDefault} onChange={function(e){setAReissueFeeDefault(e.target.value)}} placeholder="비우면 변경 안 함"/><small style={cs}>거래처 재교부료를 <b>0 으로 두거나 비워 두면</b> 이 금액이 적용됩니다</small></div>}
            {sec('기타')}
            <div><label style={ls}>기본 수수료 (표시용)</label><input style={is} type="number" value={aFee} onChange={function(e){setAFee(e.target.value)}}/><small style={cs}>요청 등록 화면의 대행업체 목록에만 보이는 참고 금액입니다. <b>정산 계산에는 쓰이지 않습니다.</b></small></div>
            <div style={wide}><label style={ls}>메모</label><input style={is} value={aMemo} onChange={function(e){setAMemo(e.target.value)}}/></div>
          </div>
          <div style={{display:'flex',gap:8,marginTop:16}}>
            <button className="btn btn-blue" style={tapBtn} onClick={saveAgency}>{isCancelAgency?'저장':(editAgency?'수정':'등록')}</button>
            {!isCancelAgency && <button className="btn btn-outline" style={tapBtn} onClick={clearAgencyForm}>취소</button>}
          </div>
        </div>)}
        {!isCancelAgency && <div className="card" style={isMobile?{padding:12}:undefined}>
          <div style={isMobile?hScroll:undefined}>
          <table className="data-table sticky-head" style={isMobile?{minWidth:760}:undefined}>
            <thead><tr><th>업체명</th><th>사업자번호</th><th>대표</th><th>전화</th><th>계좌</th><th>기본수수료</th><th>관리</th></tr></thead>
            <tbody>
              {agencies.map(function(a){return(
                <tr key={a.id} style={{opacity:a.active?1:0.5}}>
                  <td style={{fontWeight:700}}>{a.name}</td>
                  <td className="mono">{a.bizNo||'-'}</td>
                  <td>{a.ceo||'-'}</td>
                  <td>{a.tel||'-'}</td>
                  <td style={{fontSize:11}}>{a.bankInfo||'-'}</td>
                  <td style={{textAlign:'right'}}>{fmt(a.fee)} 원</td>
                  <td style={isMobile?{whiteSpace:'nowrap'}:undefined}>
                    <button className="btn btn-outline btn-sm" onClick={function(){startEditAgency(a)}} style={Object.assign({marginRight:4},tapRowBtn)}>수정</button>
                    <button className="btn btn-outline btn-sm btn-danger" style={tapRowBtn} onClick={function(){deleteAgency(a.id,a.name)}}>삭제</button>
                  </td>
                </tr>)})}
              {agencies.length===0&&(<tr><td colSpan="7" style={{padding:40,textAlign:'center',color:'#94a3b8'}}>등록된 대행업체가 없습니다.</td></tr>)}
            </tbody>
          </table>
          </div>
        </div>}
        </div>}
      </div>)}

      {/* ── 정산 탭 (장부형 W6) — 상태·UI 는 CancelAgencySettlement 소유. 탭 밖에서도 마운트 유지(선택 보존). */}
      <CancelAgencySettlement
        active={tab==='settlement'}
        user={user}
        showToast={showToast}
        isCancelAgency={isCancelAgency}
        agencies={agencies}
        agencySearch={agencySearch}
        setAgencySearch={setAgencySearch}
        openPdf={openPdf}
        focusAgency={stlFocus}
        onActivate={function(){goTab('settlement')}}
      />

      {/* 보류 사유 선택 모달 — 카테고리(구조화) + 상세 메모 */}
      {holdModal && <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
        <div style={{background:'#fff',borderRadius:12,padding:isMobile?18:24,width:380,maxWidth:'94vw',maxHeight:'90vh',overflowY:'auto'}}>
          <h3 style={{margin:'0 0 6px'}}>보류 처리</h3>
          {!holdModal._scrap&&presenceBanner(holdModal.id)}
          <p style={{margin:'0 0 14px',fontSize:13,color:'#64748b'}}>{(holdModal.carNo||'')} — 진행 불가 사유를 선택하세요</p>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:14}}>
            {HOLD_REASONS.map(function(reason){var sel=holdCat===reason;return(
              <button key={reason} className="btn" style={{justifyContent:'center',fontSize:13,minHeight:isMobile?44:undefined,border:sel?'2px solid #1B3A5C':'1px solid #d0d5dd',background:sel?'#eff6ff':'#fff',color:sel?'#1B3A5C':'#475569',fontWeight:sel?700:400}} onClick={function(){setHoldCat(reason);}}>{reason}</button>);})}
          </div>
          <label style={{fontSize:11,fontWeight:600,color:'#64748b',display:'block',marginBottom:4}}>상세 메모 (선택)</label>
          <textarea value={holdDetail} onChange={function(e){setHoldDetail(e.target.value)}} placeholder="예: 신용보증기금 압류, 해제 진행 중" rows={2} style={{width:'100%',padding:'8px 10px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:13,resize:'vertical',boxSizing:'border-box'}}/>
          <div style={{display:'flex',gap:8,marginTop:16}}>
            <button className="btn btn-blue" style={Object.assign({flex:1,justifyContent:'center'},tapBtn)} onClick={confirmHold}>보류 처리</button>
            <button className="btn btn-outline" style={Object.assign({justifyContent:'center',color:'#94a3b8'},tapBtn)} onClick={function(){setHoldModal(null);}}>취소</button>
          </div>
        </div>
      </div>}

      {delModal && <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
        <div style={{background:'#fff',borderRadius:12,padding:isMobile?18:24,width:380,maxWidth:'94vw',maxHeight:'90vh',overflowY:'auto'}}>
          <h3 style={{margin:'0 0 6px'}}>{delModal.title}</h3>
          {delModal.message&&<p style={{margin:'0 0 14px',fontSize:13,color:'#64748b',lineHeight:1.5}}>{delModal.message}</p>}
          <label style={{fontSize:11,fontWeight:600,color:'#64748b',display:'block',marginBottom:4}}>취소 사유 (선택) — 업체 알림에 함께 전달됩니다</label>
          <textarea value={delReason} onChange={function(e){setDelReason(e.target.value)}} placeholder="(선택) 업체에 전달할 취소 사유" rows={2} maxLength={200} style={{width:'100%',padding:'8px 10px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:13,resize:'vertical',boxSizing:'border-box'}}/>
          <div style={{display:'flex',gap:8,marginTop:16}}>
            <button className="btn" style={Object.assign({flex:1,justifyContent:'center',background:'#e24b4a',color:'#fff',border:'none'},tapBtn)} onClick={confirmDelete}>삭제</button>
            <button className="btn btn-outline" style={Object.assign({justifyContent:'center',color:'#94a3b8'},tapBtn)} onClick={function(){setDelModal(null);}}>취소</button>
          </div>
        </div>
      </div>}

      {/* 대행 요청 상세 패널 — 4세그먼트 표/카드 + 완료 장부 공용 행 클릭 진입 (2026-09-17) */}
      <CancelRequestDetailPanel
        open={!!detailReq}
        request={detailReq}
        badges={detailReq ? reqBadges(detailReq) : null}
        isMobile={isMobile}
        isStaff={isStaff}
        isCancelAgency={isCancelAgency}
        docList={docList}
        statusSelect={reqStatusSelect}
        onOpenDocs={openDocSheet}
        onEdit={function(r){setDetailReq(null);startEditReq(r);}}
        onClose={function(){setDetailReq(null)}}
        showToast={showToast}
      />

      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>
  );
}

// ═══ 대행요청 목록 — 기간/업체 필터 + 헤더 정렬 (순수 함수) ═══════════════════
// 컴포넌트가 1300줄이라 렌더 없이 계약을 고정할 수 있게 로직만 떼어 export 한다.
// 계약은 __tests__/CancelAgencyRequests.test.js 가 지킨다.

// 기간 필터의 기준 열. 완료 세그먼트는 완료일 — "이번 달 완료분" 이 이 탭에서 묻는 질문이다.
// (그 외 세그먼트는 아직 완료일이 비어 있어 요청일 말고는 기준이 될 수 없다.)
export function reqDateFieldFor(seg) {
  return seg === 'done' ? 'completedDate' : 'requestDate';
}

function isoDay(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

// 완료·보류 세그먼트 월 내비 — 'YYYY-MM' → 그 달 전체 [from,to] (정산 현황 ‹월› 위젯 정합, Jin 8/26).
// 형식이 아니면 빈 범위 = 무제한 (행이 몰래 사라지는 것보다 낫다).
export function reqMonthRange(ym) {
  var m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  if (!m) return { from: '', to: '' };
  return { from: ym + '-01', to: isoDay(new Date(+m[1], +m[2], 0)) };
}

// 날짜 문자열이 [from,to] 안인지. 값이 비면 제외 — 기간을 물었는데 날짜 없는 행이 섞이면 셈이 틀어진다.
// from/to 는 한쪽만 채워도 동작한다(열린 구간).
export function reqInRange(value, from, to) {
  var v = String(value || '').slice(0, 10);
  if (!v) return false;
  if (from && v < from) return false;
  if (to && v > to) return false;
  return true;
}

// 업체 드롭다운 옵션 — 유니크 업체명 + 건수. 많은 순 → 이름 순.
export function reqCompanyOptions(rows, nameOf) {
  var pick = nameOf || function (r) { return r.agencyName || '미지정'; };
  var map = {};
  (rows || []).forEach(function (r) {
    var n = pick(r);
    if (!n) return;
    map[n] = (map[n] || 0) + 1;
  });
  return Object.keys(map).map(function (n) { return { name: n, count: map[n] }; })
    .sort(function (a, b) { return b.count - a.count || a.name.localeCompare(b.name, 'ko'); });
}

// 세그먼트로 이미 좁힌 rows 에 검색·업체·기간을 AND 로 겹친다.
// 검색 대상 열 — 대행업체명 · **수출업체명** · 차량번호 · 차종 · 소유자.
//   수출업체명(requesterCompanyName)은 2026-08-26 추가. 대행사 계정의 업체 열은 requesterCompanyName 인데
//   검색은 agencyName 만 보고 있어(대행사 시야에선 늘 자기 회사) 업체명으로 아무것도 안 걸리던 결함.
//   관리자 계정에서도 "이 수출업체가 올린 건" 을 찾을 수 있어야 하므로 계정 종류로 분기하지 않는다.
export function filterReqRows(rows, opt) {
  var o = opt || {};
  var q = String(o.search || '').trim().toLowerCase();
  var pick = o.nameOf || function (r) { return r.agencyName || '미지정'; };
  var field = o.dateField || 'requestDate';
  var from = o.from || '', to = o.to || '';
  return (rows || []).filter(function (r) {
    if (o.company && pick(r) !== o.company) return false;
    if ((from || to) && !reqInRange(r[field], from, to)) return false;
    if (q) {
      var hit = (r.agencyName || '').toLowerCase().indexOf(q) !== -1
        || (r.requesterCompanyName || '').toLowerCase().indexOf(q) !== -1
        || (r.carNo || '').toLowerCase().indexOf(q) !== -1
        || (r.carType || '').toLowerCase().indexOf(q) !== -1
        || (r.owner || '').toLowerCase().indexOf(q) !== -1;
      if (!hit) return false;
    }
    return true;
  });
}

// 헤더 정렬 비교자 — key: 'requestDate'|'completedDate'|'carNo'|'company'.
// 빈 값은 방향과 무관하게 뒤로(거래처 화면 cmpBy 관례) — 정렬을 뒤집어도 "미입력"이 위로 오지 않게.
export function cmpReqBy(key, dir, nameOf) {
  var sign = dir === 'desc' ? -1 : 1;
  var pick = nameOf || function (r) { return r.agencyName || '미지정'; };
  var val = function (r) {
    return String((key === 'company' ? pick(r) : r[key]) || '').trim();
  };
  return function (a, b) {
    var av = val(a), bv = val(b);
    if (!av !== !bv) return av ? -1 : 1;
    var r = (key === 'company' || key === 'carNo') ? av.localeCompare(bv, 'ko') : av.localeCompare(bv);
    if (r) return r * sign;
    return String(b.requestDate || '').localeCompare(String(a.requestDate || '')); // 동점은 최신 신청 먼저
  };
}

export default CancelAgencyPage;
